import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseAdminPath, buildAdminPath } from '../../utils/adminPaths';
import { Settings, Code, Shield, Plus, Edit3, Trash2, Save, X, ExternalLink, ChevronRight, ArrowLeft, AlertCircle, GripVertical, ChevronDown, ChevronUp, Lock, FileText, Download, Upload, Copy, Check, Layout, ArrowDown, AlignLeft, Search, Table, Package, Users, CheckCircle, User, Briefcase, DollarSign, Zap, GitBranch, Workflow, CheckSquare, Clock, ShieldCheck } from 'lucide-react';
import { getCustomSuffix } from '../../config/gobodoConfig';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import EmailTemplatesAdmin from './EmailTemplatesAdmin';
import NotesAdminTab from '../admin/NotesAdminTab';
import DataRetentionDashboard from '../admin/DataRetentionDashboard';
import DataSensitivity from '../admin/DataSensitivity';
import DataSensitivityManager from '../admin/DataSensitivityManager';
import AdminLayout from '../admin/layout/AdminLayout';
import AdminCard from '../admin/components/AdminCard';
import FormField from '../admin/components/FormField';
import TabNavigation, { Tab } from '../admin/components/TabNavigation';
import UserManagement from '../admin/tabs/UserManagement';
import { EmailTemplatesHome } from '../admin/administration/EmailTemplatesHome';
import { NotesHome } from '../admin/administration/NotesHome';
import NotesManagementTab from '../admin/tabs/NotesManagementTab';
import DataRetentionTab from '../admin/tabs/DataRetentionTab';
import DatabaseTab from '../admin/tabs/DatabaseTab';
import MonitoringTab from '../admin/tabs/MonitoringTab';
import SecurityTab from '../admin/tabs/SecurityTab';
import APIManagementTab from '../admin/tabs/APIManagementTab';
import SystemLogsTab from '../admin/tabs/SystemLogsTab';
import ListViewManager from '../admin/ListViewManager';
import UserManager from '../admin/UserManager';
import ThemeSettingsManager from '../admin/ThemeSettingsManager';
import NavigationConfigManager from '../admin/NavigationConfigManager';
import { AppSettingsHome } from '../admin/AppSettingsHome';
import DataRetentionManager from '../admin/DataRetentionManager';
import BulkDataManager from '../admin/BulkDataManager';
import SetupHome from '../admin/SetupHome';
import { ObjectManagerHome } from '../admin/ObjectManagerHome';
import { MassDeleteManager } from '../admin/MassDeleteManager';
import { WorkflowRulesManager } from '../admin/WorkflowRulesManager';
import WorkflowRulesList from '../admin/process-automation/WorkflowRulesList';
import { ObjectDetail } from '../admin/ObjectDetail';
import { UsersHome } from '../admin/administration/UsersHome';
import { RolesHome } from '../admin/administration/RolesHome';
import { SecurityAccessHome } from '../admin/SecurityAccessHome';
import { SecurityHealthDashboard } from '../admin/SecurityHealthDashboard';
import { ScheduledJobsList, ScheduledJobWizard, JobDetailView } from '../admin/scheduled-jobs';
import {
  ApprovalProcessesList,
  ApprovalProcessWizard,
  ApprovalProcessDetail,
  PendingApprovalsQueue
} from '../admin/approval-processes';
import {
  ProcessBuilderList,
  ProcessBuilderWizard,
  ProcessBuilderDetail
} from '../admin/process-builder';
import {
  FlowList,
  FlowWizard,
  FlowDetail
} from '../admin/flow-automation';
import { processBuilderStorage } from '../../services/processBuilderStorage';
import { flowAutomationStorage } from '../../services/flowAutomationStorage';
import * as processBuilderStorageRouter from '../../services/processBuilderStorageRouter';
import * as flowAutomationStorageRouter from '../../services/flowAutomationStorageRouter';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import type { Process, ProcessExecution } from '../../types/processBuilder';
import type { Flow, FlowRun } from '../../types/flowAutomation';
import { ComponentLibraryDemo } from '../../pages/ComponentLibraryDemo';
import { ToastProvider as StandardToastProvider } from '../standard';
import { useToast } from '../../contexts/ToastContext';
import { mockUsers } from '../../data/mockUsers';
import {
  getPageLayoutByType,
  savePageLayout,
  PageLayoutSection,
  PageLayoutField
} from '../../services/pageLayoutStorage';
import { getTabsForPage, getTabIcon } from '../../services/dynamicLayoutService';
import { RecentItemsProvider } from '../../contexts/RecentItemsContext';
import UIConfigurationManager from '../../pages/admin/UIConfigurationManager';

interface SystemAdminProps {
  userRole?: string;
  onNavigate?: (view: string) => void;
  onRoleChange?: (role: string) => void;
}

interface CustomField {
  id: string;
  label: string;
  fieldKey: string;
  type: string;
  application: string;
  section: string;
  required: boolean;
  placeholder?: string;
  options?: string;
}

type FieldType = {
  id: string;
  label: string;
  type: string;
  pii: boolean;
  required: boolean;
  visible: boolean;
  width: number;
  encrypted?: boolean;
  encryptionLevel?: 'none' | 'standard' | 'high';
  autoPopulate?: boolean;
  accessRoles?: string[];
  masking?: {
    enabled: boolean;
    pattern: string;
    unmaskRoles: string[];
  };
};

interface PageSection {
  id: string;
  name: string;
  columns: number;
  visible: boolean;
  collapsible: boolean;
  application: string;
  page?: string;
  tab?: string;
  order: number;
  fields: FieldType[];
}

interface SectionFormState {
  name: string;
  columns: number;
  visible: boolean;
  collapsible: boolean;
  page?: string;
  tab?: string;
}

// ============================================
// MOCK OBJECT CARD COMPONENT
// ============================================

interface MockObjectCardProps {
  icon: string;
  label: string;
  apiName: string;
  fieldCount: number;
  badge?: string;
}

const MockObjectCard: React.FC<MockObjectCardProps> = ({
  icon,
  label,
  apiName,
  fieldCount,
  badge
}) => {
  // Map icon name to actual icon component
  const iconMap: Record<string, any> = {
    Users,
    FileText,
    CheckCircle,
    User,
    Briefcase,
    DollarSign
  };
  const IconComponent = iconMap[icon] || Package;

  return (
    <div className={`
      relative border rounded-lg p-4 transition-all
      ${badge ? 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-500 hover:shadow-md cursor-pointer'}
    `}>
      {badge && (
        <span className="absolute top-2 right-2 px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs font-medium rounded">
          {badge}
        </span>
      )}

      <div className="flex items-start space-x-3">
        <div className={`
          flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
          ${badge ? 'bg-gray-200 dark:bg-gray-700' : 'bg-purple-100 dark:bg-purple-900/30'}
        `}>
          <IconComponent className={`w-5 h-5 ${badge ? 'text-gray-400 dark:text-gray-500' : 'text-purple-600 dark:text-purple-400'}`} />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold mb-1 ${badge ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
            {label}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{apiName}</p>
          <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
            <span>{fieldCount} fields</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// COMING SOON PLACEHOLDER COMPONENT
// ============================================

interface ComingSoonPlaceholderProps {
  icon: any;
  title: string;
  description: string;
  featureList: string[];
}

const ComingSoonPlaceholder: React.FC<ComingSoonPlaceholderProps> = ({
  icon: Icon,
  title,
  description,
  featureList
}) => {
  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Coming Soon Banner */}
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-purple-100 dark:bg-purple-900/30 rounded-full p-4">
              <Icon className="w-12 h-12 text-purple-600 dark:text-purple-400" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-purple-900 dark:text-purple-100 mb-3">
            {title}
          </h1>

          <p className="text-purple-700 dark:text-purple-300 mb-6">
            {description}
          </p>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 text-left max-w-2xl mx-auto">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
              Coming Soon Features:
            </h2>
            <ul className="space-y-2">
              {featureList.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <ChevronRight className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-sm text-purple-600 dark:text-purple-400 mt-6">
            This feature will be available in a future update
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN SYSTEM ADMIN COMPONENT
// ============================================

const SystemAdmin: React.FC<SystemAdminProps> = ({ userRole, onNavigate, onRoleChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const parsed = useMemo(() => parseAdminPath(location.pathname), [location.pathname]);

  const activeSection = parsed.section;
  const selectedObjectId = parsed.objectId ?? null;
  const selectedApprovalProcessId = parsed.approvalId ?? null;
  const selectedProcessId = parsed.processId ?? null;
  const selectedFlowId = parsed.flowId ?? null;
  const selectedJobId = parsed.jobId ?? null;

  const [approvalEditMode, setApprovalEditMode] = useState(false);
  const [processBuilderEditMode, setProcessBuilderEditMode] = useState(false);
  const [flowEditMode, setFlowEditMode] = useState(false);
  const [jobEditMode, setJobEditMode] = useState(false);

  const approvalProcessesView = (approvalEditMode && parsed.approvalView === 'detail')
    ? 'edit'
    : (parsed.approvalView ?? 'list');
  const processBuilderView = (processBuilderEditMode && parsed.processView === 'detail')
    ? 'edit'
    : (parsed.processView ?? 'list');
  const flowAutomationView = (flowEditMode && parsed.flowView === 'detail')
    ? 'edit'
    : (parsed.flowView ?? 'list');
  const scheduledJobsView = (jobEditMode && parsed.jobView === 'detail')
    ? 'edit'
    : (parsed.jobView ?? 'list');

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [uiConfigTab, setUiConfigTab] = useState('system-settings');
  const [selectedPageLayoutApp, setSelectedPageLayoutApp] = useState<string | null>(null);
  const [pageLayoutView, setPageLayoutView] = useState<'main' | 'sections' | 'fields' | 'pii' | 'templates'>('main');
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [deletingSectionId, setDeletingSectionId] = useState<string | null>(null);
  const [addingFieldToSection, setAddingFieldToSection] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<{ sectionId: string; fieldId: string } | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showOnlyPIIFields, setShowOnlyPIIFields] = useState(false);
  const [expandedPIIFields, setExpandedPIIFields] = useState<Set<string>>(new Set());
  const [showSaveTemplateForm, setShowSaveTemplateForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    makePublic: false
  });
  const [cloneTemplateId, setCloneTemplateId] = useState<string | null>(null);
  const [rollbackPreviewVersion, setRollbackPreviewVersion] = useState<LayoutVersion | null>(null);
  const [showLayoutPreview, setShowLayoutPreview] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  const [processExecutions, setProcessExecutions] = useState<ProcessExecution[]>([]);
  const [processBuilderRefreshKey, setProcessBuilderRefreshKey] = useState(0);

  const [selectedFlow, setSelectedFlow] = useState<Flow | null>(null);
  const [flowRuns, setFlowRuns] = useState<FlowRun[]>([]);
  const [flowAutomationRefreshKey, setFlowAutomationRefreshKey] = useState(0);

  const [previewPage, setPreviewPage] = useState('client-detail');
  const [previewActiveTab, setPreviewActiveTab] = useState('overview');
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const [isUserTyping, setIsUserTyping] = useState(false);
  const autoSaveTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const [loadingTemplateId, setLoadingTemplateId] = useState<string | null>(null);
  const [newSection, setNewSection] = useState<SectionFormState>({
    name: '',
    columns: 2,
    visible: true,
    collapsible: true,
    page: undefined,
    tab: undefined
  });
  const { success, error } = useToast();

  useEffect(() => {
    const processId = parsed.processId;
    if (parsed.processView !== 'detail' || !processId) {
      setSelectedProcess(null);
      setProcessExecutions([]);
      return;
    }
    let cancelled = false;
    processBuilderStorageRouter.getProcessAsync(processId).then(process => {
      if (cancelled) return;
      setSelectedProcess(process ?? null);
      setProcessExecutions(
        USE_GOBODO_API ? [] : processBuilderStorage.getExecutionsForProcess(processId)
      );
    });
    return () => { cancelled = true; };
  }, [parsed.processId, parsed.processView]);

  useEffect(() => {
    const flowId = parsed.flowId;
    if (parsed.flowView !== 'detail' || !flowId) {
      setSelectedFlow(null);
      setFlowRuns([]);
      return;
    }
    let cancelled = false;
    flowAutomationStorageRouter.getFlowAsync(flowId).then(flow => {
      if (cancelled) return;
      setSelectedFlow(flow ?? null);
      setFlowRuns(USE_GOBODO_API ? [] : flowAutomationStorage.getFlowRuns(flowId));
    });
    return () => { cancelled = true; };
  }, [parsed.flowId, parsed.flowView]);

  useEffect(() => {
    setApprovalEditMode(false);
    setProcessBuilderEditMode(false);
    setFlowEditMode(false);
    setJobEditMode(false);
  }, [location.pathname]);

  // Navigation Source Tracking (for breadcrumbs and context)
  const [navigationSource, setNavigationSource] = useState<{
    from?: string;
    objectId?: string;
    objectName?: string;
  } | null>(null);

  // System Settings State
  const [systemSettings, setSystemSettings] = useState({
    applicationName: 'Regal Product Team System',
    sessionTimeout: '30',
    maintenanceMode: false,
    cacheDuration: '24',
    enableCompression: true,
    enableCDN: false
  });
  const [fieldManagementState, setFieldManagementState] = useState({
    showAddField: false,
    editingField: null as string | null,
    deletingFieldId: null as string | null,
    deleteConfirmText: '',
    validationErrors: {} as Record<string, string>,
    searchQuery: '',
    typeFilter: 'all',
    newField: {
      label: '',
      fieldKey: '',
      type: 'text',
      application: 'credit-management',
      section: 'overview',
      required: false,
      placeholder: '',
      options: ''
    }
  });

  // Template type definition
  type LayoutTemplate = {
    templateId: string;
    name: string;
    description: string;
    applicationId: string;
    createdBy: string;
    createdAt: Date;
    isPublic: boolean;
    layout: {
      sections: Array<{
        id: string;
        name: string;
        columns: number;
        visible: boolean;
        collapsible: boolean;
        application: string;
        order: number;
        fields: FieldType[];
      }>;
    };
  };

  // Version history type definition
  type LayoutVersion = {
    versionId: string;
    version: number;
    applicationId: string;
    createdAt: Date;
    createdBy: string;
    changes: string;
    snapshot: {
      sections: PageSection[];
    };
  };

  // Page Layout Sections State
  const [pageSections, setPageSections] = useState<PageSection[]>(() => {
    const availableFields: FieldType[] = [
      {
        id: 'field-1', label: 'First Name', type: 'text', pii: true, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-2', label: 'Last Name', type: 'text', pii: true, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-3', label: 'Email', type: 'email', pii: true, required: false, visible: true, width: 50,
        encryptionLevel: 'standard', autoPopulate: true, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-4', label: 'Phone', type: 'phone', pii: true, required: false, visible: true, width: 50,
        encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
        masking: { enabled: true, pattern: '(XXX) XXX-1234', unmaskRoles: ['Admin'] }
      },
      {
        id: 'field-5', label: 'SSN', type: 'ssn', pii: true, encrypted: true, required: false, visible: true, width: 50,
        encryptionLevel: 'high', autoPopulate: false, accessRoles: ['Admin'],
        masking: { enabled: true, pattern: 'XXX-XX-1234', unmaskRoles: ['Admin'] }
      },
      {
        id: 'field-6', label: 'Date of Birth', type: 'date', pii: true, required: false, visible: true, width: 50,
        encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-7', label: 'City', type: 'text', pii: false, required: false, visible: true, width: 33,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-8', label: 'State', type: 'select', pii: false, required: false, visible: true, width: 33,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-9', label: 'ZIP Code', type: 'text', pii: false, required: false, visible: true, width: 33,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-10', label: 'Client ID', type: 'text', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-11', label: 'Credit Score', type: 'number', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-12', label: 'Dispute Status', type: 'select', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-13', label: 'Case Number', type: 'text', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-14', label: 'Address Line 1', type: 'text', pii: true, required: false, visible: true, width: 100,
        encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-15', label: 'PAW Status', type: 'select', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-16', label: 'Last Activity', type: 'datetime', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-17', label: 'Bureau', type: 'select', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-18', label: 'Account Status', type: 'select', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-19', label: 'Name', type: 'text', pii: true, required: false, visible: true, width: 100,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-20', label: 'Segment', type: 'select', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-21', label: 'Status', type: 'select', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-22', label: 'Owner', type: 'text', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-23', label: 'Risk Tier', type: 'select', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-24', label: 'Last Contact', type: 'date', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-25', label: 'Products', type: 'text', pii: false, required: false, visible: true, width: 100,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-26', label: 'Representative', type: 'text', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-27', label: 'Account Name', type: 'text', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-28', label: 'Account Owner', type: 'text', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-29', label: 'Spouse', type: 'text', pii: true, required: false, visible: true, width: 50,
        encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-30', label: 'Mobile Phone', type: 'phone', pii: true, required: false, visible: true, width: 50,
        encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
        masking: { enabled: true, pattern: '(XXX) XXX-1234', unmaskRoles: ['Admin'] }
      },
      {
        id: 'field-31', label: 'Primary Parent', type: 'text', pii: true, required: false, visible: true, width: 50,
        encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-32', label: 'Birthday', type: 'date', pii: true, required: false, visible: true, width: 50,
        encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-33', label: 'Team Email', type: 'email', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-34', label: 'Email CCs', type: 'text', pii: false, required: false, visible: true, width: 100,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-35', label: 'Estimated Net Worth', type: 'currency', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-36', label: 'Annual Salary', type: 'currency', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-37', label: 'Employer', type: 'text', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-38', label: 'Service Management', type: 'text', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-39', label: 'Account Manager', type: 'text', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-40', label: 'Account Manager Company', type: 'text', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
      {
        id: 'field-41', label: 'Day To Day Contact', type: 'text', pii: false, required: false, visible: true, width: 50,
        encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
        masking: { enabled: false, pattern: '', unmaskRoles: [] }
      },
    ];

    return [
      {
        id: '1',
        name: 'Client Overview',
        columns: 2,
        visible: true,
        collapsible: true,
        application: 'global',
        order: 1,
        fields: [
          availableFields[0], // First Name
          availableFields[1], // Last Name
          availableFields[9], // Client ID
          availableFields[17], // Account Status
        ]
      },
      {
        id: '2',
        name: 'Contact Information',
        columns: 2,
        visible: true,
        collapsible: true,
        application: 'global',
        order: 2,
        fields: [
          availableFields[2], // Email
          availableFields[3], // Phone
          availableFields[13], // Address Line 1
          availableFields[6], // City
          availableFields[7], // State
          availableFields[8], // ZIP Code
        ]
      },
      {
        id: '3',
        name: 'Credit Details',
        columns: 2,
        visible: true,
        collapsible: true,
        application: 'credit-management',
        order: 1,
        fields: [
          availableFields[10], // Credit Score
          availableFields[16], // Bureau
          availableFields[4], // SSN
          availableFields[5], // Date of Birth
        ]
      },
      {
        id: '4',
        name: 'Dispute Information',
        columns: 1,
        visible: true,
        collapsible: true,
        application: 'credit-management',
        order: 2,
        fields: [
          availableFields[12], // Case Number
          availableFields[11], // Dispute Status
          availableFields[15], // Last Activity
        ]
      },
      {
        id: '5',
        name: 'CyberSweep Client Info',
        columns: 2,
        visible: true,
        collapsible: true,
        application: 'cybersweep',
        order: 1,
        fields: [
          availableFields[0], // First Name
          availableFields[1], // Last Name
          availableFields[2], // Email
          availableFields[3], // Phone
          availableFields[4], // SSN
          availableFields[5], // Date of Birth
        ]
      },
      {
        id: '6',
        name: 'PAW Records',
        columns: 1,
        visible: true,
        collapsible: true,
        application: 'cybersweep',
        order: 2,
        fields: [
          availableFields[14], // PAW Status
          availableFields[12], // Case Number
          availableFields[15], // Last Activity
        ]
      },
      // Clients Record - Overview Tab (Regal Main)
      {
        id: '7',
        name: 'Basic Information',
        columns: 1,
        visible: true,
        collapsible: true,
        application: 'regal-main',
        page: 'clients-record',
        tab: 'overview',
        order: 1,
        fields: [
          availableFields[26], // Account Name
          availableFields[27], // Account Owner
          availableFields[28], // Spouse
          availableFields[30], // Primary Parent
          availableFields[31], // Birthday
          availableFields[4], // SSN
        ]
      },
      {
        id: '8',
        name: 'Contact Information',
        columns: 1,
        visible: true,
        collapsible: true,
        application: 'regal-main',
        page: 'clients-record',
        tab: 'overview',
        order: 2,
        fields: [
          availableFields[3], // Phone
          availableFields[29], // Mobile Phone
          availableFields[2], // Email
          availableFields[32], // Team Email
          availableFields[33], // Email CCs
        ]
      },
      {
        id: '10',
        name: 'Financial & Employment',
        columns: 1,
        visible: true,
        collapsible: true,
        application: 'regal-main',
        page: 'clients-record',
        tab: 'overview',
        order: 3,
        fields: [
          availableFields[34], // Estimated Net Worth
          availableFields[35], // Annual Salary
          availableFields[36], // Employer
        ]
      },
      {
        id: '11',
        name: 'Account Management',
        columns: 1,
        visible: true,
        collapsible: true,
        application: 'regal-main',
        page: 'clients-record',
        tab: 'overview',
        order: 4,
        fields: [
          availableFields[37], // Service Management
          availableFields[38], // Account Manager
          availableFields[39], // Account Manager Company
          availableFields[40], // Day To Day Contact
          availableFields[19], // Segment
          availableFields[20], // Status
          availableFields[22], // Risk Tier
          availableFields[25], // Representative
        ]
      },
      {
        id: '12',
        name: 'Active Products & Services',
        columns: 1,
        visible: true,
        collapsible: true,
        application: 'regal-main',
        page: 'clients-record',
        tab: 'overview',
        order: 5,
        fields: [
          availableFields[24], // Products
        ]
      },
      // Clients Record - Tasks Tab (Placeholder for future)
      {
        id: '13',
        name: 'Task List',
        columns: 1,
        visible: true,
        collapsible: false,
        application: 'regal-main',
        page: 'clients-record',
        tab: 'tasks',
        order: 1,
        fields: [
          availableFields[12], // Case Number
          availableFields[20], // Status
          availableFields[15], // Last Activity
        ]
      },
      {
        id: '9',
        name: 'Bureau Management',
        columns: 2,
        visible: true,
        collapsible: true,
        application: 'credit-block',
        order: 1,
        fields: [
          availableFields[16], // Bureau
          availableFields[17], // Account Status
          availableFields[10], // Credit Score
        ]
      },
    ];
  });

  // Available Fields Library
  const availableFields: FieldType[] = [
    {
      id: 'field-1', label: 'First Name', type: 'text', pii: true, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-2', label: 'Last Name', type: 'text', pii: true, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-3', label: 'Email', type: 'email', pii: true, required: false, visible: true, width: 50,
      encryptionLevel: 'standard', autoPopulate: true, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-4', label: 'Phone', type: 'phone', pii: true, required: false, visible: true, width: 50,
      encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
      masking: { enabled: true, pattern: '(XXX) XXX-1234', unmaskRoles: ['Admin'] }
    },
    {
      id: 'field-5', label: 'SSN', type: 'ssn', pii: true, encrypted: true, required: false, visible: true, width: 50,
      encryptionLevel: 'high', autoPopulate: false, accessRoles: ['Admin'],
      masking: { enabled: true, pattern: 'XXX-XX-1234', unmaskRoles: ['Admin'] }
    },
    {
      id: 'field-6', label: 'Date of Birth', type: 'date', pii: true, required: false, visible: true, width: 50,
      encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-7', label: 'City', type: 'text', pii: false, required: false, visible: true, width: 33,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-8', label: 'State', type: 'select', pii: false, required: false, visible: true, width: 33,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-9', label: 'ZIP Code', type: 'text', pii: false, required: false, visible: true, width: 33,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-10', label: 'Client ID', type: 'text', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-11', label: 'Credit Score', type: 'number', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-12', label: 'Dispute Status', type: 'select', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-13', label: 'Case Number', type: 'text', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-14', label: 'Address Line 1', type: 'text', pii: true, required: false, visible: true, width: 100,
      encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-15', label: 'PAW Status', type: 'select', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-16', label: 'Last Activity', type: 'datetime', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-17', label: 'Bureau', type: 'select', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-18', label: 'Account Status', type: 'select', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-19', label: 'Name', type: 'text', pii: true, required: false, visible: true, width: 100,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-20', label: 'Segment', type: 'select', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-21', label: 'Status', type: 'select', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-22', label: 'Owner', type: 'text', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-23', label: 'Risk Tier', type: 'select', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-24', label: 'Last Contact', type: 'date', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-25', label: 'Products', type: 'text', pii: false, required: false, visible: true, width: 100,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-26', label: 'Representative', type: 'text', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-27', label: 'Account Name', type: 'text', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-28', label: 'Account Owner', type: 'text', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-29', label: 'Spouse', type: 'text', pii: true, required: false, visible: true, width: 50,
      encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-30', label: 'Mobile Phone', type: 'phone', pii: true, required: false, visible: true, width: 50,
      encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
      masking: { enabled: true, pattern: '(XXX) XXX-1234', unmaskRoles: ['Admin'] }
    },
    {
      id: 'field-31', label: 'Primary Parent', type: 'text', pii: true, required: false, visible: true, width: 50,
      encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-32', label: 'Birthday', type: 'date', pii: true, required: false, visible: true, width: 50,
      encryptionLevel: 'standard', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-33', label: 'Team Email', type: 'email', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-34', label: 'Email CCs', type: 'text', pii: false, required: false, visible: true, width: 100,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-35', label: 'Estimated Net Worth', type: 'currency', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-36', label: 'Annual Salary', type: 'currency', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-37', label: 'Employer', type: 'text', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-38', label: 'Service Management', type: 'text', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-39', label: 'Account Manager', type: 'text', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-40', label: 'Account Manager Company', type: 'text', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
    {
      id: 'field-41', label: 'Day To Day Contact', type: 'text', pii: false, required: false, visible: true, width: 50,
      encryptionLevel: 'none', autoPopulate: false, accessRoles: ['Admin', 'Manager', 'User'],
      masking: { enabled: false, pattern: '', unmaskRoles: [] }
    },
  ];

  // Load saved layout on mount or when app changes
  useEffect(() => {
    if (selectedPageLayoutApp) {
      try {
        // Load from centralized pageLayouts storage
        const allLayouts = localStorage.getItem('pageLayouts');
        if (allLayouts) {
          const layouts = JSON.parse(allLayouts);

          // Find the layout for this app
          const layoutKey = Object.keys(layouts).find(key =>
            layouts[key].applicationId === selectedPageLayoutApp
          );

          if (layoutKey) {
            const layoutData = layouts[layoutKey];
            const appSections = layoutData.sections.filter((s: any) => s.application === selectedPageLayoutApp);

            // Merge saved sections with existing ones (keep sections for other apps)
            setPageSections(prev => {
              const otherAppSections = prev.filter(s => s.application !== selectedPageLayoutApp);
              return [...otherAppSections, ...appSections];
            });

            setHasUnsavedChanges(false);
          }
        }
      } catch (err) {
        console.error('Error loading saved layout:', err);
      }
    }
  }, [selectedPageLayoutApp]);

  // Auto-save effect
  useEffect(() => {
    // Don't auto-save if user is typing or no changes
    if (!hasUnsavedChanges || isUserTyping || !selectedPageLayoutApp) {
      return;
    }

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer for 2 seconds
    autoSaveTimerRef.current = setTimeout(() => {
      setAutoSaveStatus('saving');
      handleSaveLayout('Auto-save');
    }, 2000);

    // Cleanup
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasUnsavedChanges, isUserTyping, selectedPageLayoutApp, pageSections]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (hasUnsavedChanges && selectedPageLayoutApp) {
          handleSaveLayout();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasUnsavedChanges, selectedPageLayoutApp]);

  // Confirmation on navigation/page leave if unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Save layout to localStorage using page layout storage service
  const handleSaveLayout = (changeDescription?: string, isAutoSave = false) => {
    if (!selectedPageLayoutApp) return;

    const currentUser = mockUsers.find(u => u.role === 'system_admin');

    try {
      // Get or create page layout for this application
      let existingLayout = getPageLayoutByType('client', selectedPageLayoutApp);

      if (!existingLayout) {
        // Create new layout if it doesn't exist
        existingLayout = {
          id: `page-layout-client-${selectedPageLayoutApp}`,
          name: `Client Page Layout (${selectedPageLayoutApp})`,
          objectType: 'client',
          applicationId: selectedPageLayoutApp,
          sections: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }

      // Update sections for this application
      const appSections: PageLayoutSection[] = pageSections
        .filter(s => s.application === selectedPageLayoutApp)
        .map(section => ({
          id: section.id,
          name: section.name,
          columns: section.columns,
          visible: section.visible,
          collapsible: section.collapsible,
          application: section.application,
          page: section.page,
          tab: section.tab,
          order: section.order,
          fields: section.fields as PageLayoutField[]
        }));

      existingLayout.sections = appSections;
      savePageLayout(existingLayout);

      // Also save to old format for backwards compatibility with version history
      const layoutData = {
        applicationId: selectedPageLayoutApp,
        sections: pageSections.filter(s => s.application === selectedPageLayoutApp),
        lastModified: new Date().toISOString(),
        modifiedBy: currentUser?.email || 'admin@company.com'
      };
      localStorage.setItem(`layout-${selectedPageLayoutApp}`, JSON.stringify(layoutData));

      // Create version history entry
      const versions: LayoutVersion[] = JSON.parse(localStorage.getItem('layout-versions') || '[]');
      const appVersions = versions.filter(v => v.applicationId === selectedPageLayoutApp);
      const nextVersion = appVersions.length > 0 ? Math.max(...appVersions.map(v => v.version)) + 1 : 1;

      const versionEntry: LayoutVersion = {
        versionId: `version-${Date.now()}`,
        version: nextVersion,
        applicationId: selectedPageLayoutApp,
        createdAt: new Date(),
        createdBy: currentUser?.email || 'admin@company.com',
        changes: changeDescription || 'Layout configuration updated',
        snapshot: {
          sections: pageSections.filter(s => s.application === selectedPageLayoutApp)
        }
      };

      versions.push(versionEntry);
      localStorage.setItem('layout-versions', JSON.stringify(versions));

      setHasUnsavedChanges(false);

      if (isAutoSave || changeDescription === 'Auto-save') {
        setAutoSaveStatus('saved');
        // Reset to idle after 3 seconds
        setTimeout(() => setAutoSaveStatus('idle'), 3000);
      } else {
        success('Layout saved', `Page layout for ${selectedPageLayoutApp} has been saved successfully (Version ${nextVersion}).`);
      }
    } catch (err) {
      console.error('Error saving layout:', err);

      if (isAutoSave || changeDescription === 'Auto-save') {
        setAutoSaveStatus('failed');
      } else {
        // Determine error type for better messaging
        let errorMsg = 'Failed to save page layout. ';
        if (err instanceof Error) {
          if (err.name === 'QuotaExceededError') {
            errorMsg += 'Storage quota exceeded. Please clear some space and try again.';
          } else {
            errorMsg += `Error: ${err.message}`;
          }
        } else {
          errorMsg += 'An unknown error occurred.';
        }

        // Show error with retry option
        if (window.confirm(`${errorMsg}\n\nWould you like to retry saving?`)) {
          handleSaveLayout(changeDescription, isAutoSave);
        }
      }
    }
  };

  const [customFields, setCustomFields] = useState<CustomField[]>([
    // Credit Management Fields
    { id: '1', label: 'Custom Score Target', fieldKey: 'customScoreTarget__c', type: 'number', application: 'credit-management', section: 'overview', required: false, placeholder: '', options: '' },
    { id: '2', label: 'Special Notes', fieldKey: 'specialNotes__c', type: 'textarea', application: 'credit-management', section: 'overview', required: false, placeholder: '', options: '' },
    { id: '3', label: 'Referral Source', fieldKey: 'referralSource__c', type: 'select', application: 'credit-management', section: 'overview', required: false, placeholder: '', options: '' },

    // CyberSweep Client Fields (36 fields)
    { id: '4', label: 'First Name', fieldKey: 'firstName__c', type: 'text', application: 'cybersweep', section: 'cs-client', required: true, placeholder: '', options: '' },
    { id: '5', label: 'Last Name', fieldKey: 'lastName__c', type: 'text', application: 'cybersweep', section: 'cs-client', required: true, placeholder: '', options: '' },
    { id: '6', label: 'CyberSweep Start Date', fieldKey: 'cyberSweepStartDate__c', type: 'date', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '7', label: 'CyberSweep End Date', fieldKey: 'cyberSweepEndDate__c', type: 'date', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '8', label: 'CyberSweep Stage', fieldKey: 'cyberSweepStage__c', type: 'select', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '9', label: 'CyberSweep Status', fieldKey: 'cyberSweepStatus__c', type: 'select', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '10', label: 'Client Personal Phone', fieldKey: 'clientPersonalPhone__c', type: 'tel', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '11', label: 'Personal Email Address', fieldKey: 'personalEmailAddress__c', type: 'email', application: 'cybersweep', section: 'cs-client', required: true, placeholder: '', options: '' },
    { id: '12', label: 'Current Home Address', fieldKey: 'currentHomeAddress__c', type: 'text', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '13', label: 'Current City', fieldKey: 'currentCity__c', type: 'text', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '14', label: 'Current State', fieldKey: 'currentState__c', type: 'text', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '15', label: 'Current Zip/Postal', fieldKey: 'currentZipPostal__c', type: 'text', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '16', label: 'Previous Home Address', fieldKey: 'previousHomeAddress__c', type: 'text', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '17', label: 'Previous City', fieldKey: 'previousCity__c', type: 'text', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '18', label: 'Previous State', fieldKey: 'previousState__c', type: 'text', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '19', label: 'Previous Zip/Postal', fieldKey: 'previousZipPostal__c', type: 'text', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '20', label: 'CS Reports Recipients', fieldKey: 'csReportsRecipients__c', type: 'text', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '21', label: 'Initial Report', fieldKey: 'initialReport__c', type: 'checkbox', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '22', label: 'Initial Report Date', fieldKey: 'initialReportDate__c', type: 'date', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '23', label: 'Final Report', fieldKey: 'finalReport__c', type: 'checkbox', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '24', label: 'Final Report Date', fieldKey: 'finalReportDate__c', type: 'date', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '25', label: 'Quarterly Sweep 1', fieldKey: 'quarterlySweep1__c', type: 'checkbox', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '26', label: 'Sweep 1 Date', fieldKey: 'sweep1Date__c', type: 'date', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '27', label: 'Quarterly Sweep 2', fieldKey: 'quarterlySweep2__c', type: 'checkbox', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '28', label: 'Sweep 2 Date', fieldKey: 'sweep2Date__c', type: 'date', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '29', label: 'Quarterly Sweep 3', fieldKey: 'quarterlySweep3__c', type: 'checkbox', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '30', label: 'Sweep 3 Date', fieldKey: 'sweep3Date__c', type: 'date', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '31', label: 'Quarterly Sweep 4', fieldKey: 'quarterlySweep4__c', type: 'checkbox', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '32', label: 'Sweep 4 Date', fieldKey: 'sweep4Date__c', type: 'date', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '33', label: 'Profession', fieldKey: 'profession__c', type: 'text', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '34', label: 'Other Profession', fieldKey: 'otherProfession__c', type: 'text', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '35', label: 'Dummy Email Address', fieldKey: 'dummyEmailAddress__c', type: 'email', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },
    { id: '36', label: 'Dummy Password', fieldKey: 'dummyPassword__c', type: 'text', application: 'cybersweep', section: 'cs-client', required: false, placeholder: '', options: '' },

    // CyberSweep PAW Record Fields (10 fields)
    { id: '37', label: 'PAW ID', fieldKey: 'id__c', type: 'text', application: 'cybersweep', section: 'paw-record', required: true, placeholder: '', options: '' },
    { id: '38', label: 'Data Broker Website', fieldKey: 'website__c', type: 'text', application: 'cybersweep', section: 'paw-record', required: true, placeholder: '', options: '' },
    { id: '39', label: 'Audit Template', fieldKey: 'auditTemplate__c', type: 'text', application: 'cybersweep', section: 'paw-record', required: false, placeholder: '', options: '' },
    { id: '40', label: 'Audit Phase', fieldKey: 'auditPhase__c', type: 'select', application: 'cybersweep', section: 'paw-record', required: false, placeholder: '', options: '' },
    { id: '41', label: 'PII Found', fieldKey: 'piiFound__c', type: 'checkbox', application: 'cybersweep', section: 'paw-record', required: false, placeholder: '', options: '' },
    { id: '42', label: 'Findings', fieldKey: 'findings__c', type: 'textarea', application: 'cybersweep', section: 'paw-record', required: false, placeholder: '', options: '' },
    { id: '43', label: 'Method', fieldKey: 'method__c', type: 'text', application: 'cybersweep', section: 'paw-record', required: false, placeholder: '', options: '' },
    { id: '44', label: 'Phase 2 Required', fieldKey: 'phase2Required__c', type: 'checkbox', application: 'cybersweep', section: 'paw-record', required: false, placeholder: '', options: '' },
    { id: '45', label: 'Created By', fieldKey: 'createdBy__c', type: 'text', application: 'cybersweep', section: 'paw-record', required: false, placeholder: '', options: '' },
    { id: '46', label: 'Client Info Links', fieldKey: 'clientInfoLinks__c', type: 'textarea', application: 'cybersweep', section: 'paw-record', required: false, placeholder: '', options: '' },

    // CyberSweep CS Audit Fields (11 fields)
    { id: '47', label: 'Auditor', fieldKey: 'auditor__c', type: 'text', application: 'cybersweep', section: 'cs-audit', required: false, placeholder: '', options: '' },
    { id: '48', label: 'Due Date', fieldKey: 'dueDate__c', type: 'date', application: 'cybersweep', section: 'cs-audit', required: false, placeholder: '', options: '' },
    { id: '49', label: 'Fiscal Year', fieldKey: 'fiscalYear__c', type: 'text', application: 'cybersweep', section: 'cs-audit', required: false, placeholder: '', options: '' },
    { id: '50', label: 'Audit Type', fieldKey: 'auditType__c', type: 'select', application: 'cybersweep', section: 'cs-audit', required: true, placeholder: '', options: '' },
    { id: '51', label: 'Phase 1 Due Date', fieldKey: 'phase1DueDate__c', type: 'date', application: 'cybersweep', section: 'cs-audit', required: false, placeholder: '', options: '' },
    { id: '52', label: 'Phase 2 Due Date', fieldKey: 'phase2DueDate__c', type: 'date', application: 'cybersweep', section: 'cs-audit', required: false, placeholder: '', options: '' },
    { id: '53', label: 'Phase Status', fieldKey: 'phaseStatus__c', type: 'select', application: 'cybersweep', section: 'cs-audit', required: false, placeholder: '', options: '' },
    { id: '54', label: 'Roadblock Note', fieldKey: 'roadBlockNote__c', type: 'textarea', application: 'cybersweep', section: 'cs-audit', required: false, placeholder: '', options: '' },
    { id: '55', label: 'Created By', fieldKey: 'createdBy__c', type: 'text', application: 'cybersweep', section: 'cs-audit', required: false, placeholder: '', options: '' },
    { id: '56', label: 'Created Date', fieldKey: 'createdDate__c', type: 'date', application: 'cybersweep', section: 'cs-audit', required: false, placeholder: '', options: '' },
    { id: '57', label: 'Last Modified By', fieldKey: 'lastModifiedBy__c', type: 'text', application: 'cybersweep', section: 'cs-audit', required: false, placeholder: '', options: '' },
  ]);

  if (userRole !== 'system_admin') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-8">
        <div className="text-center">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400">You need System Administrator privileges to access this section.</p>
        </div>
      </div>
    );
  }

  const renderFieldManagement = () => {

    const validateField = (): boolean => {
      const errors: Record<string, string> = {};
      const field = fieldManagementState.newField;
      const isEditMode = fieldManagementState.editingField !== null;

      // Field Label validation
      if (!field.label || field.label.trim() === '') {
        errors.label = 'Field label is required';
      } else if (field.label.length > 40) {
        errors.label = 'Field label too long (max 40 characters)';
      }

      // Field Key validation
      if (!field.fieldKey || field.fieldKey.trim() === '') {
        errors.fieldKey = 'Field key is required';
      } else {
        // Check valid characters (only letters, numbers, underscores, no starting with number)
        // Remove custom suffix before validation
        const suffix = getCustomSuffix();
        const suffixRegex = new RegExp(`${suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`);
        if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(field.fieldKey.replace(suffixRegex, ''))) {
          errors.fieldKey = 'Field key contains invalid characters (only letters, numbers, underscores; cannot start with number)';
        }

        // Check length
        if (field.fieldKey.length > 40) {
          errors.fieldKey = 'Field key too long (max 40 characters)';
        }

        // Check uniqueness (only when adding or if key changed during edit)
        const keyToCheck = field.fieldKey.endsWith('__c') ? field.fieldKey : `${field.fieldKey}__c`;
        const isDuplicate = customFields.some(f => {
          const existingKey = f.fieldKey.endsWith('__c') ? f.fieldKey : `${f.fieldKey}__c`;
          return existingKey === keyToCheck && f.id !== fieldManagementState.editingField;
        });

        if (isDuplicate) {
          errors.fieldKey = 'Field key must be unique';
        }
      }

      // Type-specific validation
      if (field.type === 'number') {
        // Number validation would require additional fields for length and decimal places
        // This is a placeholder for when those fields are added to the form
      }

      if (field.type === 'picklist' || field.type === 'multipicklist') {
        if (!field.options || field.options.trim() === '') {
          errors.options = 'Picklist must have at least one value';
        }
      }

      if (field.type === 'url' && field.placeholder) {
        if (field.placeholder && !field.placeholder.match(/^https?:\/\//)) {
          errors.placeholder = 'URL must start with http:// or https://';
        }
      }

      setFieldManagementState(prev => ({
        ...prev,
        validationErrors: errors
      }));

      return Object.keys(errors).length === 0;
    };

    const handleSaveField = () => {
      // Validate field
      if (!validateField()) {
        return;
      }

      const isEditMode = fieldManagementState.editingField !== null;

      // Auto-add __c suffix if not present
      let fieldKey = fieldManagementState.newField.fieldKey;
      if (!fieldKey.endsWith('__c')) {
        fieldKey = `${fieldKey}__c`;
      }

      // Show confirmation dialog for Master-Detail relationships when adding
      if (!isEditMode && fieldManagementState.newField.type === 'master-detail') {
        const confirmed = window.confirm(
          'Master-Detail relationships permanently delete child records when master is deleted. Continue?'
        );
        if (!confirmed) {
          return;
        }
      }

      // Show confirmation dialog when editing
      if (isEditMode) {
        const confirmed = window.confirm(
          'Modifying this field may affect existing data. Continue?'
        );
        if (!confirmed) {
          return;
        }
      }

      if (isEditMode) {
        // Update existing field
        setCustomFields(prev => prev.map(field =>
          field.id === fieldManagementState.editingField
            ? { ...field, ...fieldManagementState.newField, fieldKey, id: field.id }
            : field
        ));
        console.log('Updating field:', { ...fieldManagementState.newField, fieldKey });
      } else {
        // Add new field
        const newField = {
          id: String(Date.now()),
          ...fieldManagementState.newField,
          fieldKey
        };
        setCustomFields(prev => [...prev, newField]);
        console.log('Adding field:', newField);
      }

      // Reset form
      setFieldManagementState(prev => ({
        ...prev,
        showAddField: false,
        editingField: null,
        validationErrors: {},
        newField: {
          label: '',
          fieldKey: '',
          type: 'text',
          application: 'credit-management',
          section: 'overview',
          required: false,
          placeholder: '',
          options: ''
        }
      }));
    };

    const handleDeleteFieldClick = (fieldId: string) => {
      setFieldManagementState(prev => ({
        ...prev,
        deletingFieldId: fieldId,
        deleteConfirmText: ''
      }));
    };

    const handleConfirmDelete = () => {
      const fieldToDelete = customFields.find(f => f.id === fieldManagementState.deletingFieldId);
      if (!fieldToDelete) return;

      // Delete the field
      setCustomFields(prev => prev.filter(f => f.id !== fieldManagementState.deletingFieldId));

      console.log('Field deleted:', fieldToDelete);

      // Reset state
      setFieldManagementState(prev => ({
        ...prev,
        deletingFieldId: null,
        deleteConfirmText: ''
      }));

      // Show success message
      success(`Field '${fieldToDelete.label}' deleted successfully`);
    };

    const handleCancelDelete = () => {
      setFieldManagementState(prev => ({
        ...prev,
        deletingFieldId: null,
        deleteConfirmText: ''
      }));
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">Field Management</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">Manage custom fields across all applications</p>
          </div>
          <button
            onClick={() => setFieldManagementState(prev => ({ ...prev, showAddField: true }))}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Custom Field
          </button>
        </div>

        {/* Add/Edit Field Modal/Form */}
        {fieldManagementState.showAddField && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h5 className="font-medium text-gray-900 dark:text-gray-100">
                {fieldManagementState.editingField ? 'Edit Custom Field' : 'Add New Custom Field'}
              </h5>
              <button
                onClick={() => setFieldManagementState(prev => ({
                  ...prev,
                  showAddField: false,
                  editingField: null,
                  validationErrors: {},
                  newField: {
                    label: '',
                    fieldKey: '',
                    type: 'text',
                    application: 'credit-management',
                    section: 'overview',
                    required: false,
                    placeholder: '',
                    options: ''
                  }
                }))}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Field Label *
                </label>
                <input
                  type="text"
                  value={fieldManagementState.newField.label}
                  onChange={(e) => setFieldManagementState(prev => ({
                    ...prev,
                    newField: { ...prev.newField, label: e.target.value },
                    validationErrors: { ...prev.validationErrors, label: '' }
                  }))}
                  className={`w-full px-3 py-2 border ${
                    fieldManagementState.validationErrors.label
                      ? 'border-red-500 dark:border-red-500'
                      : 'border-gray-300 dark:border-gray-600'
                  } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 ${
                    fieldManagementState.validationErrors.label ? 'focus:ring-red-500' : 'focus:ring-blue-500'
                  }`}
                  placeholder="e.g., Custom Score Target"
                />
                {fieldManagementState.validationErrors.label && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {fieldManagementState.validationErrors.label}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Field Key *
                </label>
                <input
                  type="text"
                  value={fieldManagementState.newField.fieldKey}
                  onChange={(e) => setFieldManagementState(prev => ({
                    ...prev,
                    newField: { ...prev.newField, fieldKey: e.target.value },
                    validationErrors: { ...prev.validationErrors, fieldKey: '' }
                  }))}
                  disabled={fieldManagementState.editingField !== null}
                  className={`w-full px-3 py-2 border ${
                    fieldManagementState.validationErrors.fieldKey
                      ? 'border-red-500 dark:border-red-500'
                      : 'border-gray-300 dark:border-gray-600'
                  } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 ${
                    fieldManagementState.validationErrors.fieldKey ? 'focus:ring-red-500' : 'focus:ring-blue-500'
                  } ${fieldManagementState.editingField !== null ? 'opacity-60 cursor-not-allowed' : ''}`}
                  placeholder="e.g., customScoreTarget"
                />
                {fieldManagementState.validationErrors.fieldKey && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {fieldManagementState.validationErrors.fieldKey}
                  </p>
                )}
                {fieldManagementState.editingField !== null && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                    API names cannot be changed after creation
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Field Type
                </label>
                <select
                  value={fieldManagementState.newField.type}
                  onChange={(e) => setFieldManagementState(prev => ({
                    ...prev,
                    newField: { ...prev.newField, type: e.target.value }
                  }))}
                  disabled={fieldManagementState.editingField !== null}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    fieldManagementState.editingField !== null ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                >
                  <optgroup label="Basic Types">
                    <option value="text">Text</option>
                    <option value="textarea">Text Area</option>
                    <option value="textarea-long">Text Area (Long)</option>
                    <option value="number">Number</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="url">URL</option>
                    <option value="checkbox">Checkbox</option>
                  </optgroup>
                  <optgroup label="Date & Time">
                    <option value="date">Date</option>
                    <option value="datetime">Date/Time</option>
                    <option value="time">Time</option>
                  </optgroup>
                  <optgroup label="Financial">
                    <option value="currency">Currency</option>
                    <option value="percent">Percent</option>
                  </optgroup>
                  <optgroup label="Selection">
                    <option value="picklist">Picklist</option>
                    <option value="multipicklist">Multi-Select Picklist</option>
                  </optgroup>
                  <optgroup label="Advanced">
                    <option value="formula">Formula</option>
                    <option value="autonumber">Auto Number</option>
                    <option value="encrypted">Encrypted Text</option>
                    <option value="geolocation">Geolocation</option>
                  </optgroup>
                  <optgroup label="Relationships">
                    <option value="lookup">Lookup Relationship</option>
                    <option value="master-detail">Master-Detail Relationship</option>
                  </optgroup>
                </select>
                {fieldManagementState.editingField !== null && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                    Field type cannot be changed after creation
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Application
                </label>
                <select
                  value={fieldManagementState.newField.application}
                  onChange={(e) => setFieldManagementState(prev => ({
                    ...prev,
                    newField: { ...prev.newField, application: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="global-view">🌐 Global View (All Applications)</option>
                  <option value="credit-management">Credit Management</option>
                  <option value="cybersweep">CyberSweep</option>
                  <option value="credit-block">Credit Block</option>
                  <option value="regal-main">Regal Main</option>
                </select>
                {fieldManagementState.newField.application === 'global-view' && (
                  <p className="mt-2 text-sm text-blue-600 dark:text-blue-400 flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400">ℹ️</span>
                    <span>Global fields appear on all client records regardless of enrolled products. Use for PII (Personal Identifiable Information) and universal client data.</span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Section / Object Type
                </label>
                <select
                  value={fieldManagementState.newField.section}
                  onChange={(e) => setFieldManagementState(prev => ({
                    ...prev,
                    newField: { ...prev.newField, section: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {/* Always show General sections */}
                  <optgroup label="General Sections">
                    <option value="overview">Overview</option>
                    <option value="personal">Personal Information</option>
                    <option value="contact">Contact Information</option>
                    <option value="financial">Financial Information</option>
                    <option value="notes">Notes</option>
                  </optgroup>

                  {/* Show CyberSweep sections only for cybersweep app */}
                  {(fieldManagementState.newField.application === 'cybersweep' || fieldManagementState.newField.application === 'global-view') && (
                    <optgroup label="CyberSweep Objects">
                      <option value="paw-record">PAW Record (Performed Audit Website)</option>
                      <option value="cs-audit">CS Audit (CyberSweep Audit)</option>
                      <option value="cs-client">CyberSweep Client</option>
                    </optgroup>
                  )}

                  {/* Show Credit Management sections only for credit-management app */}
                  {(fieldManagementState.newField.application === 'credit-management' || fieldManagementState.newField.application === 'global-view') && (
                    <optgroup label="Credit Management Objects">
                      <option value="credit-repair">Credit Repair Case</option>
                      <option value="id-theft">ID Theft Recovery</option>
                      <option value="credit-report">Credit Report Record</option>
                    </optgroup>
                  )}

                  {/* Show Credit Block sections only for credit-block app */}
                  {(fieldManagementState.newField.application === 'credit-block' || fieldManagementState.newField.application === 'global-view') && (
                    <optgroup label="Credit Block Objects">
                      <option value="freeze">Credit Freeze</option>
                      <option value="freeze-info">Credit Freeze Information</option>
                      <option value="security-info">Security Information</option>
                      <option value="bureau-info">Bureau Information</option>
                    </optgroup>
                  )}
                </select>
              </div>

              {/* Dynamic field based on type - Placeholder Text for most types */}
              {!['textarea', 'textarea-long', 'number', 'checkbox', 'currency', 'percent', 'date', 'datetime', 'time', 'autonumber', 'formula', 'encrypted', 'geolocation', 'lookup', 'master-detail'].includes(fieldManagementState.newField.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Placeholder Text
                  </label>
                  <input
                    type="text"
                    value={fieldManagementState.newField.placeholder}
                    onChange={(e) => setFieldManagementState(prev => ({
                      ...prev,
                      newField: { ...prev.newField, placeholder: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={fieldManagementState.newField.type === 'url' ? 'https://www.example.com' : 'Optional placeholder text'}
                  />
                </div>
              )}

              {/* URL specific fields */}
              {fieldManagementState.newField.type === 'url' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Link Text (optional)
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Visit Website"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      If empty, shows full URL. If filled, shows this text as clickable link.
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Open in New Window
                    </label>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <span className="text-blue-600 dark:text-blue-400 text-lg flex-shrink-0">ℹ️</span>
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          URLs must start with http:// or https://
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Lookup Relationship specific fields */}
              {fieldManagementState.newField.type === 'lookup' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Related Object
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <optgroup label="Standard Objects">
                        <option value="account">Account</option>
                        <option value="contact">Contact</option>
                        <option value="user">User</option>
                        <option value="case">Case</option>
                        <option value="opportunity">Opportunity</option>
                      </optgroup>
                      <optgroup label="Custom Objects">
                        <option value="credit-repair-case">Credit Repair Case</option>
                        <option value="cybersweep-audit">CyberSweep Audit</option>
                        <option value="paw-record">PAW Record</option>
                      </optgroup>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Child Relationship Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Auto-generated (e.g., Accounts)"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Plural form of Field Label (auto-generated)
                    </p>
                  </div>
                  <div className="col-span-2">
                    <div className="space-y-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                      <h6 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Additional Options
                      </h6>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 dark:border-gray-600"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Allow linking to multiple records
                        </span>
                      </label>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="space-y-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                      <h6 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Related List Settings
                      </h6>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          defaultChecked
                          className="rounded border-gray-300 dark:border-gray-600"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Show on related object
                        </span>
                      </label>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Related List Label
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Auto-generated from field label"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <span className="text-blue-600 dark:text-blue-400 text-lg flex-shrink-0">ℹ️</span>
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          Lookup relationships are optional connections. Deleting the parent record does not delete child records.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Master-Detail Relationship specific fields */}
              {fieldManagementState.newField.type === 'master-detail' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Master Object
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <optgroup label="Standard Objects">
                        <option value="account">Account</option>
                        <option value="contact">Contact</option>
                        <option value="case">Case</option>
                        <option value="opportunity">Opportunity</option>
                      </optgroup>
                      <optgroup label="Custom Objects">
                        <option value="credit-repair-case">Credit Repair Case</option>
                        <option value="cybersweep-audit">CyberSweep Audit</option>
                        <option value="paw-record">PAW Record</option>
                      </optgroup>
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Select the master object (User object not allowed)
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Child Relationship Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Auto-generated from Field Label"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Auto-generated if left blank
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Related List Settings
                    </label>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <input
                          type="checkbox"
                          checked
                          disabled
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-not-allowed opacity-60"
                        />
                        <span>Always shows on master object's related lists</span>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">
                          Related List Label
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Auto-generated from field label"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <span className="text-red-600 dark:text-red-400 text-xl flex-shrink-0">⚠️</span>
                        <div>
                          <h6 className="text-sm font-semibold text-red-900 dark:text-red-100 mb-2">
                            Important
                          </h6>
                          <ul className="text-sm text-red-800 dark:text-red-200 space-y-1.5">
                            <li>• This field is <strong>always required</strong></li>
                            <li>• Child records <strong>inherit security from master</strong></li>
                            <li>• Deleting master record <strong>DELETES all child records</strong></li>
                            <li>• <strong>Cannot change to Lookup</strong> after creation</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Encrypted Text specific fields */}
              {fieldManagementState.newField.type === 'encrypted' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Maximum Length
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={175}
                      defaultValue={175}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="175"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Maximum 175 characters for encrypted fields
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Mask Type
                    </label>
                    <select
                      defaultValue="all"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Characters (********)</option>
                      <option value="lastfour">Last Four Clear (****1234)</option>
                      <option value="ssn">SSN Format (***-**-1234)</option>
                      <option value="creditcard">Credit Card (•••• •••• •••• 1234)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Mask Character
                    </label>
                    <select
                      defaultValue="asterisk"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="asterisk">Asterisk (*)</option>
                      <option value="bullet">Bullet (•)</option>
                      <option value="x">X</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <span className="text-yellow-600 dark:text-yellow-400 text-lg flex-shrink-0">⚠️</span>
                        <div>
                          <h6 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
                            Security Warning
                          </h6>
                          <p className="text-sm text-yellow-800 dark:text-yellow-200">
                            Encrypted fields cannot be used in formulas, filters, or search. Only users with 'View Encrypted Data' permission can see unmasked values.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Geolocation specific fields */}
              {fieldManagementState.newField.type === 'geolocation' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Decimal Precision
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={15}
                      defaultValue={5}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="5"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      More decimals = more precise (5 decimals ≈ 1 meter accuracy)
                    </p>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <span className="text-blue-600 dark:text-blue-400 text-lg flex-shrink-0">ℹ️</span>
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          Geolocation fields store latitude and longitude coordinates. Access via {'{FieldName}'}__Latitude__s and {'{FieldName}'}__Longitude__s
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Formula specific fields */}
              {fieldManagementState.newField.type === 'formula' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Return Type
                    </label>
                    <select
                      defaultValue="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="currency">Currency</option>
                      <option value="date">Date</option>
                      <option value="datetime">Date/Time</option>
                      <option value="percent">Percent</option>
                      <option value="checkbox">Checkbox</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Decimal Places
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={17}
                      defaultValue={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="2"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      For Number, Currency, and Percent return types
                    </p>
                  </div>
                </>
              )}

            {/* Formula Editor (full width, outside grid) */}
            {fieldManagementState.newField.type === 'formula' && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Formula
                </label>
                <textarea
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="Enter your formula here..."
                  style={{ minHeight: '150px' }}
                />

                {/* Helper Buttons */}
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Check Syntax
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Insert Field
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Insert Function
                  </button>
                </div>

                {/* Formula Examples */}
                <details className="mt-4">
                  <summary className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400">
                    📚 Formula Examples
                  </summary>
                  <div className="mt-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <ul className="space-y-2 text-sm">
                      <li className="flex flex-col gap-1">
                        <span className="font-semibold text-gray-900 dark:text-gray-100">Full Name:</span>
                        <code className="font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded text-gray-700 dark:text-gray-300">
                          FirstName + " " + LastName
                        </code>
                      </li>
                      <li className="flex flex-col gap-1">
                        <span className="font-semibold text-gray-900 dark:text-gray-100">Commission:</span>
                        <code className="font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded text-gray-700 dark:text-gray-300">
                          Amount * 0.05
                        </code>
                      </li>
                      <li className="flex flex-col gap-1">
                        <span className="font-semibold text-gray-900 dark:text-gray-100">Days Overdue:</span>
                        <code className="font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded text-gray-700 dark:text-gray-300">
                          TODAY() - Due_Date__c
                        </code>
                      </li>
                    </ul>
                    <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                      💡 Formulas are always calculated and cannot be edited by users
                    </p>
                  </div>
                </details>
              </div>
            )}

              {/* Auto Number specific fields */}
              {fieldManagementState.newField.type === 'autonumber' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Display Format
                    </label>
                    <input
                      type="text"
                      maxLength={30}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="CASE-{0000}"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Use {'{0}'} for numbers. Maximum 30 characters total.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Starting Number
                    </label>
                    <input
                      type="number"
                      min={1}
                      defaultValue={1}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="1"
                    />
                  </div>
                  <div className="col-span-2">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                      <h6 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                        Format Examples
                      </h6>
                      <ul className="space-y-1 text-xs text-blue-700 dark:text-blue-300">
                        <li className="flex items-center gap-2">
                          <span className="font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded">CASE-{'{0000}'}</span>
                          <span>→</span>
                          <span>CASE-0001, CASE-0002, ...</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded">INV-{'{000000}'}</span>
                          <span>→</span>
                          <span>INV-000001, INV-000002, ...</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded">{'{0}'}-2025</span>
                          <span>→</span>
                          <span>1-2025, 2-2025, ...</span>
                        </li>
                      </ul>
                      <p className="mt-3 text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2">
                        <span>⚠️</span>
                        <span>Auto Number fields are read-only and cannot be edited by users. The field is always present (not optional).</span>
                      </p>
                    </div>
                  </div>
                </>
              )}

              {/* Date field default value */}
              {fieldManagementState.newField.type === 'date' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Default Value
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                    onChange={(e) => {
                      const showPicker = e.target.value === 'specific';
                      if (!showPicker) {
                        // Hide the date picker
                        const picker = e.target.nextElementSibling;
                        if (picker) picker.classList.add('hidden');
                      } else {
                        const picker = e.target.nextElementSibling;
                        if (picker) picker.classList.remove('hidden');
                      }
                    }}
                  >
                    <option value="none">None</option>
                    <option value="today">Today</option>
                    <option value="specific">Specific Date</option>
                  </select>
                  <input
                    type="date"
                    className="hidden w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Date/Time field default value */}
              {fieldManagementState.newField.type === 'datetime' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Default Value
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                    onChange={(e) => {
                      const showPicker = e.target.value === 'specific';
                      if (!showPicker) {
                        const picker = e.target.nextElementSibling;
                        if (picker) picker.classList.add('hidden');
                      } else {
                        const picker = e.target.nextElementSibling;
                        if (picker) picker.classList.remove('hidden');
                      }
                    }}
                  >
                    <option value="none">None</option>
                    <option value="now">Now</option>
                    <option value="specific">Specific Date/Time</option>
                  </select>
                  <input
                    type="datetime-local"
                    className="hidden w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Time field default value */}
              {fieldManagementState.newField.type === 'time' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Default Value
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                    onChange={(e) => {
                      const showPicker = e.target.value === 'specific';
                      if (!showPicker) {
                        const picker = e.target.nextElementSibling;
                        if (picker) picker.classList.add('hidden');
                      } else {
                        const picker = e.target.nextElementSibling;
                        if (picker) picker.classList.remove('hidden');
                      }
                    }}
                  >
                    <option value="none">None</option>
                    <option value="now">Now</option>
                    <option value="specific">Specific Time</option>
                  </select>
                  <input
                    type="time"
                    className="hidden w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Text Area specific fields */}
              {(fieldManagementState.newField.type === 'textarea' || fieldManagementState.newField.type === 'textarea-long') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Maximum Characters
                    </label>
                    <input
                      type="number"
                      defaultValue={255}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="255"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Number of Lines
                    </label>
                    <input
                      type="number"
                      defaultValue={3}
                      min={1}
                      max={20}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="3"
                    />
                  </div>
                </>
              )}

              {/* Number specific fields */}
              {fieldManagementState.newField.type === 'number' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Total Digits
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={18}
                      defaultValue={18}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="18"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Decimal Places
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={17}
                      defaultValue={0}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                  </div>
                </>
              )}

              {/* Number default value */}
              {fieldManagementState.newField.type === 'number' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Default Value (optional)
                  </label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
              )}

              {/* Checkbox default value */}
              {fieldManagementState.newField.type === 'checkbox' && (
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Default Checked</span>
                  </label>
                </div>
              )}

              {/* Currency specific fields */}
              {fieldManagementState.newField.type === 'currency' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Total Digits
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={18}
                      defaultValue={16}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="16"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Decimal Places
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={17}
                      defaultValue={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Currency Code
                    </label>
                    <select
                      defaultValue="USD"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                      <option value="CAD">CAD - Canadian Dollar</option>
                      <option value="AUD">AUD - Australian Dollar</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Default Value (optional)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Percent specific fields */}
              {fieldManagementState.newField.type === 'percent' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Digits Before Decimal
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={16}
                      defaultValue={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Decimal Places
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={17}
                      defaultValue={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Default Value (optional)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        max={100}
                        className="w-full pr-8 pl-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">%</span>
                    </div>
                  </div>
                </>
              )}

              {/* Picklist Values Section */}
              {(fieldManagementState.newField.type === 'picklist' ||
                fieldManagementState.newField.type === 'multipicklist') && (
                <div className="mt-4 col-span-2">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {fieldManagementState.newField.type === 'multipicklist' ? 'Multi-Select Picklist Values' : 'Picklist Values'}
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {fieldManagementState.newField.type === 'multipicklist'
                          ? 'Enter values (one per line). Users can select multiple values using Ctrl+Click (Windows) or Cmd+Click (Mac)'
                          : 'Enter values (one per line) or use the list manager below'}
                      </p>
                    </div>
                    <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 dark:border-gray-600 mr-2"
                      />
                      Sort Alphabetically
                    </label>
                  </div>

                  {/* Multi-Select Picklist: Visible Lines */}
                  {fieldManagementState.newField.type === 'multipicklist' && (
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Visible Lines in UI
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={10}
                        defaultValue={4}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="4"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        💡 Values are stored separated by semicolons (e.g., Bronze;Silver;Gold)
                      </p>
                    </div>
                  )}

                  <textarea
                    value={fieldManagementState.newField.options}
                    onChange={(e) => setFieldManagementState(prev => ({
                      ...prev,
                      newField: { ...prev.newField, options: e.target.value },
                      validationErrors: { ...prev.validationErrors, options: '' }
                    }))}
                    rows={6}
                    className={`w-full px-3 py-2 border ${
                      fieldManagementState.validationErrors.options
                        ? 'border-red-500 dark:border-red-500'
                        : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 ${
                      fieldManagementState.validationErrors.options ? 'focus:ring-red-500' : 'focus:ring-blue-500'
                    } font-mono text-sm`}
                    placeholder={"Active\nInactive\nPending\nClosed"}
                  />
                  {fieldManagementState.validationErrors.options && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {fieldManagementState.validationErrors.options}
                    </p>
                  )}

                  {/* Picklist values list (if values exist) */}
                  {fieldManagementState.newField.options && fieldManagementState.newField.options.trim().length > 0 && (
                    <div className="mt-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                      <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Picklist Values ({fieldManagementState.newField.options.split('\n').filter(v => v.trim()).length})
                      </h6>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {fieldManagementState.newField.options.split('\n').filter(v => v.trim()).map((value, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-3 bg-white dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700 group hover:border-blue-300 dark:hover:border-blue-600"
                          >
                            <div title="Drag to reorder">
                              <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />
                            </div>
                            {fieldManagementState.newField.type === 'multipicklist' ? (
                              <input
                                type="checkbox"
                                className="rounded text-blue-600"
                                title="Set as default"
                              />
                            ) : (
                              <input
                                type="radio"
                                name="defaultPicklistValue"
                                className="text-blue-600"
                                title="Set as default"
                              />
                            )}
                            <span className="flex-1 text-sm text-gray-900 dark:text-gray-100">{value.trim()}</span>
                            <button
                              type="button"
                              onClick={() => {
                                const values = fieldManagementState.newField.options.split('\n');
                                values.splice(index, 1);
                                setFieldManagementState(prev => ({
                                  ...prev,
                                  newField: { ...prev.newField, options: values.join('\n') }
                                }));
                              }}
                              className="opacity-0 group-hover:opacity-100 p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete value"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                        <span>💡</span>
                        <span>
                          Drag to reorder • {fieldManagementState.newField.type === 'multipicklist' ? 'Check boxes for default values' : 'Select radio for default value'} • Hover to delete
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Legacy select dropdown support (comma-separated) */}
              {fieldManagementState.newField.type === 'select' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Options (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={fieldManagementState.newField.options}
                    onChange={(e) => setFieldManagementState(prev => ({
                      ...prev,
                      newField: { ...prev.newField, options: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Option 1, Option 2, Option 3"
                  />
                </div>
              )}
            </div>

            {/* Required field checkbox - hide for checkbox, autonumber, formula, and geolocation types */}
            {!['checkbox', 'autonumber', 'formula', 'geolocation'].includes(fieldManagementState.newField.type) && (
              <div className="mt-4 flex items-center">
                <label className={`flex items-center ${fieldManagementState.newField.type === 'master-detail' ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <input
                    type="checkbox"
                    checked={fieldManagementState.newField.type === 'master-detail' ? true : fieldManagementState.newField.required}
                    disabled={fieldManagementState.newField.type === 'master-detail'}
                    onChange={(e) => setFieldManagementState(prev => ({
                      ...prev,
                      newField: { ...prev.newField, required: e.target.checked }
                    }))}
                    className={`rounded border-gray-300 dark:border-gray-600 ${fieldManagementState.newField.type === 'master-detail' ? 'cursor-not-allowed' : ''}`}
                  />
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                    Required field
                    {fieldManagementState.newField.type === 'master-detail' && (
                      <span className="ml-2 text-xs italic">(always required)</span>
                    )}
                  </span>
                </label>
              </div>
            )}

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setFieldManagementState(prev => ({
                  ...prev,
                  showAddField: false,
                  editingField: null,
                  validationErrors: {},
                  newField: {
                    label: '',
                    fieldKey: '',
                    type: 'text',
                    application: 'credit-management',
                    section: 'overview',
                    required: false,
                    placeholder: '',
                    options: ''
                  }
                }))}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveField}
                disabled={!fieldManagementState.newField.label || !fieldManagementState.newField.fieldKey}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                {fieldManagementState.editingField ? 'Update Field' : 'Add Field'}
              </button>
            </div>
          </div>
        )}

        {/* Search and Filter */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search fields by name or type..."
                value={fieldManagementState.searchQuery}
                onChange={(e) => setFieldManagementState(prev => ({ ...prev, searchQuery: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Type Filter Dropdown */}
            <div className="sm:w-48">
              <select
                value={fieldManagementState.typeFilter}
                onChange={(e) => setFieldManagementState(prev => ({ ...prev, typeFilter: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="text">Text</option>
                <option value="textarea">Text Area</option>
                <option value="textarea-long">Text Area (Long)</option>
                <option value="number">Number</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="tel">Tel</option>
                <option value="url">URL</option>
                <option value="checkbox">Checkbox</option>
                <option value="date">Date</option>
                <option value="datetime">Date/Time</option>
                <option value="time">Time</option>
                <option value="currency">Currency</option>
                <option value="percent">Percent</option>
                <option value="picklist">Picklist</option>
                <option value="select">Select</option>
                <option value="multipicklist">Multi-Select Picklist</option>
                <option value="formula">Formula</option>
                <option value="autonumber">Auto Number</option>
                <option value="encrypted">Encrypted Text</option>
                <option value="geolocation">Geolocation</option>
                <option value="lookup">Lookup Relationship</option>
                <option value="master-detail">Master-Detail Relationship</option>
              </select>
            </div>
          </div>
        </div>

        {/* Existing Fields List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h5 className="font-medium text-gray-900 dark:text-gray-100">Custom Fields</h5>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Showing {customFields.filter(field => {
                const searchLower = fieldManagementState.searchQuery.toLowerCase();
                const matchesSearch = !fieldManagementState.searchQuery ||
                  field.label.toLowerCase().includes(searchLower) ||
                  field.fieldKey.toLowerCase().includes(searchLower) ||
                  field.type.toLowerCase().includes(searchLower) ||
                  field.application.toLowerCase().includes(searchLower) ||
                  field.section.toLowerCase().includes(searchLower);
                const matchesType = fieldManagementState.typeFilter === 'all' || field.type === fieldManagementState.typeFilter;
                return matchesSearch && matchesType;
              }).length} of {customFields.length} fields
            </span>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {customFields.length === 0 && (
              <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                No custom fields configured. Add your first custom field above.
              </div>
            )}
            {customFields.length > 0 && customFields.filter(field => {
              const searchLower = fieldManagementState.searchQuery.toLowerCase();
              const matchesSearch = !fieldManagementState.searchQuery ||
                field.label.toLowerCase().includes(searchLower) ||
                field.fieldKey.toLowerCase().includes(searchLower) ||
                field.type.toLowerCase().includes(searchLower) ||
                field.application.toLowerCase().includes(searchLower) ||
                field.section.toLowerCase().includes(searchLower);
              const matchesType = fieldManagementState.typeFilter === 'all' || field.type === fieldManagementState.typeFilter;
              return matchesSearch && matchesType;
            }).length === 0 && (
              <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                No fields match your search criteria.
              </div>
            )}
            {customFields.length > 0 && customFields.filter(field => {
              const searchLower = fieldManagementState.searchQuery.toLowerCase();
              const matchesSearch = !fieldManagementState.searchQuery ||
                field.label.toLowerCase().includes(searchLower) ||
                field.fieldKey.toLowerCase().includes(searchLower) ||
                field.type.toLowerCase().includes(searchLower) ||
                field.application.toLowerCase().includes(searchLower) ||
                field.section.toLowerCase().includes(searchLower);
              const matchesType = fieldManagementState.typeFilter === 'all' || field.type === fieldManagementState.typeFilter;
              return matchesSearch && matchesType;
            }).map((field) => (
              <div key={field.id} className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4">
                      <div>
                        <h6 className="font-medium text-gray-900 dark:text-gray-100">{field.label}</h6>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {field.fieldKey} • {field.type} • {field.application} • {field.section}
                        </p>
                      </div>
                      {field.required && (
                        <span className="px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-full">
                          Required
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setFieldManagementState(prev => ({
                          ...prev,
                          showAddField: true,
                          editingField: field.id,
                          newField: {
                            label: field.label,
                            fieldKey: field.fieldKey,
                            type: field.type,
                            application: field.application,
                            section: field.section,
                            required: field.required,
                            placeholder: '',
                            options: ''
                          }
                        }));
                      }}
                      className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteFieldClick(field.id)}
                      className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {fieldManagementState.deletingFieldId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Delete Custom Field?
                </h3>
                <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                  <p>
                    Are you sure you want to delete <span className="font-semibold text-gray-900 dark:text-gray-100">'{customFields.find(f => f.id === fieldManagementState.deletingFieldId)?.label}'</span>?
                  </p>
                  <p className="text-red-600 dark:text-red-400 font-medium">
                    This action cannot be undone. All data in this field will be permanently deleted.
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 space-y-1">
                    <p className="font-medium text-gray-700 dark:text-gray-300">Field Details:</p>
                    <p>• Name: {customFields.find(f => f.id === fieldManagementState.deletingFieldId)?.label}</p>
                    <p>• Key: {customFields.find(f => f.id === fieldManagementState.deletingFieldId)?.fieldKey}</p>
                    <p>• Type: {customFields.find(f => f.id === fieldManagementState.deletingFieldId)?.type}</p>
                    <p>• Application: {customFields.find(f => f.id === fieldManagementState.deletingFieldId)?.application}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Type <span className="font-bold text-red-600">DELETE</span> to confirm:
                    </label>
                    <input
                      type="text"
                      value={fieldManagementState.deleteConfirmText}
                      onChange={(e) => setFieldManagementState(prev => ({
                        ...prev,
                        deleteConfirmText: e.target.value
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      placeholder="Type DELETE"
                      autoFocus
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={fieldManagementState.deleteConfirmText !== 'DELETE'}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const uiConfigTabs: Tab[] = [
    { id: 'system-settings', label: 'Settings' },  // Consolidated: Theme + System settings
    { id: 'navigation', label: 'Navigation' }
  ];

  const renderUIConfiguration = () => (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm text-gray-600 dark:text-gray-400" aria-label="Breadcrumb">
        <span>Setup</span>
        <ChevronRight className="h-4 w-4 mx-2" />
        <span className="text-gray-900 dark:text-gray-100 font-medium">UI Configuration</span>
      </nav>

      {/* Tab Navigation */}
      <TabNavigation
        tabs={uiConfigTabs}
        activeTab={uiConfigTab}
        onTabChange={setUiConfigTab}
      />

      {/* Tab Content */}
      {uiConfigTab === 'system-settings' && (
        <ThemeSettingsManager />
      )}

      {uiConfigTab === 'navigation' && (
        <NavigationConfigManager />
      )}
    </div>
  );

  const handleSaveSystemSettings = () => {
    console.log('Saving system settings:', systemSettings);
    success('Settings saved successfully', 'Your system settings have been updated.');
  };

  const handleAddSection = () => {
    // Validation: Section name cannot be empty
    if (!newSection.name.trim()) {
      error('Section name required', 'Please enter a section name.');
      return;
    }

    if (!selectedPageLayoutApp) return;

    // Validation: Section names must be unique within the application
    const currentSections = pageSections.filter(s => s.application === selectedPageLayoutApp);
    const duplicateName = currentSections.find(s =>
      s.name.toLowerCase() === newSection.name.trim().toLowerCase() &&
      s.id !== editingSectionId
    );

    if (duplicateName) {
      error('Duplicate section name', `A section named "${newSection.name}" already exists in this application.`);
      return;
    }

    if (editingSectionId) {
      // Update existing section
      setPageSections(pageSections.map(section =>
        section.id === editingSectionId
          ? {
              ...section,
              name: newSection.name.trim(),
              columns: newSection.columns as 1 | 2 | 3,
              visible: newSection.visible,
              collapsible: newSection.collapsible,
              page: newSection.page,
              tab: newSection.tab
            }
          : section
      ));
      success('Section updated', `"${newSection.name}" has been updated successfully.`);
    } else {
      // Add new section
      const maxOrder = currentSections.length > 0 ? Math.max(...currentSections.map(s => s.order)) : 0;

      const sectionToAdd = {
        id: Date.now().toString(),
        name: newSection.name.trim(),
        columns: newSection.columns as 1 | 2 | 3,
        visible: newSection.visible,
        collapsible: newSection.collapsible,
        application: selectedPageLayoutApp,
        page: newSection.page,
        tab: newSection.tab,
        order: maxOrder + 1,
        fields: []
      };

      setPageSections([...pageSections, sectionToAdd]);
      success('Section added', `"${newSection.name}" has been added successfully.`);
    }

    setHasUnsavedChanges(true);
    setShowAddSectionModal(false);
    setEditingSectionId(null);
    setNewSection({
      name: '',
      columns: 2,
      visible: true,
      collapsible: true,
      page: undefined,
      tab: undefined
    });
  };

  const handleEditSection = (sectionId: string) => {
    const section = pageSections.find(s => s.id === sectionId);
    if (section) {
      setNewSection({
        name: section.name,
        columns: section.columns,
        visible: section.visible,
        collapsible: section.collapsible,
        page: section.page,
        tab: section.tab
      });
      setEditingSectionId(sectionId);
      setShowAddSectionModal(true);
    }
  };

  const handleDeleteSection = () => {
    if (!deletingSectionId) return;

    // Validation: Check if section contains required fields
    const sectionToDelete = pageSections.find(s => s.id === deletingSectionId);
    if (!sectionToDelete) return;

    const requiredFields = sectionToDelete.fields.filter(f => f.required);
    if (requiredFields.length > 0) {
      const fieldNames = requiredFields.map(f => f.label).join(', ');
      error(
        'Cannot delete section',
        `This section contains required fields: ${fieldNames}. Please remove these fields or mark them as optional before deleting the section.`
      );
      setDeletingSectionId(null);
      return;
    }

    setPageSections(pageSections.filter(s => s.id !== deletingSectionId));
    success('Section deleted', `"${sectionToDelete.name}" has been deleted successfully.`);
    setHasUnsavedChanges(true);
    setDeletingSectionId(null);
  };

  const handleAddFieldToSection = (sectionId: string, fieldId: string) => {
    const field = availableFields.find(f => f.id === fieldId);
    if (!field) return;

    // Check if field is already in any section
    const isFieldUsed = pageSections.some(section =>
      section.fields.some((f) => f.id === fieldId)
    );

    if (isFieldUsed) {
      error('Field already in use', 'This field has already been added to a section.');
      return;
    }

    // Validation: Field labels must be unique within a section
    const targetSection = pageSections.find(s => s.id === sectionId);
    if (targetSection) {
      const duplicateLabel = targetSection.fields.find(f =>
        f.label.toLowerCase() === field.label.toLowerCase()
      );

      if (duplicateLabel) {
        error('Duplicate field label', `A field with the label "${field.label}" already exists in this section.`);
        return;
      }
    }

    // Add field to section
    setPageSections(pageSections.map(section =>
      section.id === sectionId
        ? { ...section, fields: [...section.fields, field] }
        : section
    ));

    setHasUnsavedChanges(true);
    setAddingFieldToSection(null);
    success('Field added', `"${field.label}" has been added to the section.`);
  };

  const handleRemoveFieldFromSection = (sectionId: string, fieldId: string) => {
    const section = pageSections.find(s => s.id === sectionId);
    const field = section?.fields.find((f) => f.id === fieldId);

    setPageSections(pageSections.map(s =>
      s.id === sectionId
        ? { ...s, fields: s.fields.filter((f) => f.id !== fieldId) }
        : s
    ));

    setHasUnsavedChanges(true);
    success('Field removed', `${field?.label} has been removed from the section.`);
  };

  // Get available fields that haven't been added to any section in the current application
  const getAvailableFieldsForSection = () => {
    // Only check for fields used in the current application's sections
    const usedFieldIds = pageSections
      .filter(section => section.application === selectedPageLayoutApp)
      .flatMap(section => section.fields.map((f) => f.id));

    return availableFields.filter(field => !usedFieldIds.includes(field.id));
  };

  const handleUpdateFieldProperty = (sectionId: string, fieldId: string, property: string, value: any) => {
    setPageSections(pageSections.map(section =>
      section.id === sectionId
        ? {
            ...section,
            fields: section.fields.map(field =>
              field.id === fieldId
                ? { ...field, [property]: value }
                : field
            )
          }
        : section
    ));
    setHasUnsavedChanges(true);
  };

  const handleTogglePIIStatus = (fieldId: string, property: 'pii' | 'encrypted') => {
    // Update the field in all sections where it appears
    setPageSections(pageSections.map(section => ({
      ...section,
      fields: section.fields.map(field =>
        field.id === fieldId
          ? { ...field, [property]: !field[property] }
          : field
      )
    })));
    setHasUnsavedChanges(true);
  };

  const handleUpdatePIIConfig = (fieldId: string, property: string, value: any) => {
    // Update the field's PII configuration in all sections where it appears
    setPageSections(pageSections.map(section => ({
      ...section,
      fields: section.fields.map(field =>
        field.id === fieldId
          ? { ...field, [property]: value }
          : field
      )
    })));
    setHasUnsavedChanges(true);
  };

  const handleSaveTemplate = () => {
    if (!newTemplate.name.trim()) {
      error('Template name required', 'Please enter a template name.');
      return;
    }

    if (!selectedPageLayoutApp) return;

    const currentUser = mockUsers.find(u => u.role === 'system_admin');
    const template: LayoutTemplate = {
      templateId: `template-${Date.now()}`,
      name: newTemplate.name,
      description: newTemplate.description,
      applicationId: selectedPageLayoutApp,
      createdBy: currentUser?.email || 'admin@company.com',
      createdAt: new Date(),
      isPublic: newTemplate.makePublic,
      layout: {
        sections: pageSections.filter(s => s.application === selectedPageLayoutApp)
      }
    };

    try {
      const templates: LayoutTemplate[] = JSON.parse(localStorage.getItem('layout-templates') || '[]');
      templates.push(template);
      localStorage.setItem('layout-templates', JSON.stringify(templates));

      success('Template saved', `"${template.name}" has been saved successfully.`);
      setShowSaveTemplateForm(false);
      setNewTemplate({ name: '', description: '', makePublic: false });
    } catch (err) {
      console.error('Error saving template:', err);

      let errorMsg = 'Could not save template. ';
      if (err instanceof Error) {
        if (err.name === 'QuotaExceededError') {
          errorMsg += 'Storage quota exceeded.';
        } else {
          errorMsg += err.message;
        }
      }

      if (window.confirm(`${errorMsg}\n\nWould you like to retry?`)) {
        handleSaveTemplate();
      }
    }
  };

  const handleLoadTemplate = (template: LayoutTemplate) => {
    if (!window.confirm('This will replace your current layout. Continue?')) {
      return;
    }

    setLoadingTemplateId(template.templateId);

    // Simulate async loading with setTimeout
    setTimeout(() => {
      // Replace current sections for this app with template sections
      setPageSections(prev => {
        const otherAppSections = prev.filter(s => s.application !== selectedPageLayoutApp);
        return [...otherAppSections, ...template.layout.sections];
      });

      setHasUnsavedChanges(true);
      setLoadingTemplateId(null);
      success('Template loaded', `"${template.name}" has been loaded successfully.`);
    }, 300);
  };

  const handleCloneTemplate = (template: LayoutTemplate, targetApp: string) => {
    const currentUser = mockUsers.find(u => u.role === 'system_admin');

    // Create cloned template with new ID and target application
    const clonedTemplate: LayoutTemplate = {
      templateId: `template-${Date.now()}`,
      name: `${template.name} (Cloned)`,
      description: template.description,
      applicationId: targetApp,
      createdBy: currentUser?.email || 'admin@company.com',
      createdAt: new Date(),
      isPublic: template.isPublic,
      layout: {
        sections: template.layout.sections.map(section => ({
          ...section,
          id: `${section.id}-cloned-${Date.now()}`,
          application: targetApp
        }))
      }
    };

    try {
      const templates: LayoutTemplate[] = JSON.parse(localStorage.getItem('layout-templates') || '[]');
      templates.push(clonedTemplate);
      localStorage.setItem('layout-templates', JSON.stringify(templates));

      setCloneTemplateId(null);

      const appName = targetApp === 'global' ? 'Global Settings' :
                     targetApp === 'regal-main' ? 'Regal Main' :
                     targetApp === 'credit-management' ? 'Credit Management' :
                     targetApp === 'cybersweep' ? 'CyberSweep' :
                     'Credit Block';

      success('Template cloned', `"${template.name}" has been cloned to ${appName}. Switch to that application to view it.`);
    } catch (err) {
      error('Clone failed', 'Could not clone template.');
      console.error('Error cloning template:', err);
    }
  };

  const handleDeleteTemplate = (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }

    try {
      const templates: LayoutTemplate[] = JSON.parse(localStorage.getItem('layout-templates') || '[]');
      const updatedTemplates = templates.filter(t => t.templateId !== templateId);
      localStorage.setItem('layout-templates', JSON.stringify(updatedTemplates));

      success('Template deleted', 'The template has been removed successfully.');
    } catch (err) {
      error('Delete failed', 'Could not delete template.');
      console.error('Error deleting template:', err);
    }
  };

  const handleRollbackVersion = (version: LayoutVersion) => {
    if (!selectedPageLayoutApp) return;

    const currentUser = mockUsers.find(u => u.role === 'system_admin');

    try {
      // Step 1: Save current state as a new version before rollback
      const versions: LayoutVersion[] = JSON.parse(localStorage.getItem('layout-versions') || '[]');
      const appVersions = versions.filter(v => v.applicationId === selectedPageLayoutApp);
      const nextVersion = appVersions.length > 0 ? Math.max(...appVersions.map(v => v.version)) + 1 : 1;

      const backupVersion: LayoutVersion = {
        versionId: `version-${Date.now()}-backup`,
        version: nextVersion,
        applicationId: selectedPageLayoutApp,
        createdAt: new Date(),
        createdBy: currentUser?.email || 'admin@company.com',
        changes: `Backup before rollback to Version ${version.version}`,
        snapshot: {
          sections: pageSections.filter(s => s.application === selectedPageLayoutApp)
        }
      };

      versions.push(backupVersion);

      // Step 2: Restore the selected version
      setPageSections(prev => {
        const otherAppSections = prev.filter(s => s.application !== selectedPageLayoutApp);
        return [...otherAppSections, ...version.snapshot.sections];
      });

      // Step 3: Save the restored layout and create rollback version entry
      const rollbackVersion: LayoutVersion = {
        versionId: `version-${Date.now()}-rollback`,
        version: nextVersion + 1,
        applicationId: selectedPageLayoutApp,
        createdAt: new Date(),
        createdBy: currentUser?.email || 'admin@company.com',
        changes: `Rolled back to Version ${version.version}`,
        snapshot: {
          sections: version.snapshot.sections
        }
      };

      versions.push(rollbackVersion);
      localStorage.setItem('layout-versions', JSON.stringify(versions));

      // Save the layout
      const layoutData = {
        applicationId: selectedPageLayoutApp,
        sections: version.snapshot.sections,
        lastModified: new Date().toISOString(),
        modifiedBy: currentUser?.email || 'admin@company.com'
      };
      localStorage.setItem(`layout-${selectedPageLayoutApp}`, JSON.stringify(layoutData));

      setHasUnsavedChanges(false);
      setRollbackPreviewVersion(null);
      success('Rollback complete', `Successfully rolled back to Version ${version.version}. Your previous layout was saved as a backup.`);
    } catch (err) {
      error('Rollback failed', 'Could not rollback to selected version.');
      console.error('Error rolling back version:', err);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;

    // Dropped outside a valid droppable area
    if (!destination) return;

    // Dropped in the same location
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Safety check: Only handle sections-list if we're in the sections view
    if ((source.droppableId === 'sections-list' || destination.droppableId === 'sections-list') && pageLayoutView !== 'sections') {
      return;
    }

    // Case 0: Reordering sections in the sections list
    if (source.droppableId === 'sections-list' && destination.droppableId === 'sections-list') {
      const filteredSections = pageSections
        .filter(section => section.application === selectedPageLayoutApp)
        .sort((a, b) => a.order - b.order);

      const reorderedSections = Array.from(filteredSections);
      const [movedSection] = reorderedSections.splice(source.index, 1);
      reorderedSections.splice(destination.index, 0, movedSection);

      // Update the order property for all affected sections
      const updatedSections = reorderedSections.map((section, index) => ({
        ...section,
        order: index + 1
      }));

      // Merge with sections from other applications
      const otherAppSections = pageSections.filter(
        section => section.application !== selectedPageLayoutApp
      );

      setPageSections([...otherAppSections, ...updatedSections]);
      setHasUnsavedChanges(true);
      success('Section reordered', 'Section order has been updated.');
      return;
    }

    // Case 1: From available fields library to a section
    if (source.droppableId === 'available-fields') {
      const field = availableFields.find(f => f.id === draggableId);
      if (!field) return;

      // Check if field is already used in the current application's sections
      const isFieldUsed = pageSections
        .filter(section => section.application === selectedPageLayoutApp)
        .some(section => section.fields.some(f => f.id === draggableId));

      if (isFieldUsed) {
        error('Field already in use', 'This field has already been added to a section in this application.');
        return;
      }

      // Add field to destination section at specific index
      const targetSectionId = destination.droppableId.replace('section-', '');
      setPageSections(pageSections.map(section =>
        section.id === targetSectionId
          ? {
              ...section,
              fields: [
                ...section.fields.slice(0, destination.index),
                field,
                ...section.fields.slice(destination.index)
              ]
            }
          : section
      ));

      setHasUnsavedChanges(true);
      success('Field added', `${field.label} has been added to the section.`);
      return;
    }

    // Case 2 & 3: Within same section or between sections
    const sourceSectionId = source.droppableId.replace('section-', '');
    const destSectionId = destination.droppableId.replace('section-', '');

    const sourceSection = pageSections.find(s => s.id === sourceSectionId);
    const draggedField = sourceSection?.fields.find(f => f.id === draggableId);

    if (!draggedField) return;

    // Case 2: Reordering within the same section
    if (sourceSectionId === destSectionId) {
      setPageSections(pageSections.map(section => {
        if (section.id === sourceSectionId) {
          const newFields = Array.from(section.fields);
          const [removed] = newFields.splice(source.index, 1);
          newFields.splice(destination.index, 0, removed);
          return { ...section, fields: newFields };
        }
        return section;
      }));

      setHasUnsavedChanges(true);
      return;
    }

    // Case 3: Moving field from one section to another
    setPageSections(pageSections.map(section => {
      // Remove from source section
      if (section.id === sourceSectionId) {
        return {
          ...section,
          fields: section.fields.filter(f => f.id !== draggableId)
        };
      }
      // Add to destination section
      if (section.id === destSectionId) {
        const newFields = [...section.fields];
        newFields.splice(destination.index, 0, draggedField);
        return { ...section, fields: newFields };
      }
      return section;
    }));

    setHasUnsavedChanges(true);
    success('Field moved', `${draggedField.label} has been moved to the new section.`);
  };

  const renderSystemSettings = () => (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm text-gray-600 dark:text-gray-400" aria-label="Breadcrumb">
        <span>Setup</span>
        <ChevronRight className="h-4 w-4 mx-2" />
        <span className="text-gray-900 dark:text-gray-100 font-medium">System Settings</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Application Settings */}
        <AdminCard
          title="Application Settings"
          description="Configure general application behavior and user session settings"
        >
          <div className="space-y-4">
            <FormField
              label="Application Name"
              name="applicationName"
              type="text"
              value={systemSettings.applicationName}
              onChange={(value) => setSystemSettings({ ...systemSettings, applicationName: value })}
              placeholder="Enter application name"
              required
            />

            <FormField
              label="Session Timeout"
              name="sessionTimeout"
              type="number"
              value={systemSettings.sessionTimeout}
              onChange={(value) => setSystemSettings({ ...systemSettings, sessionTimeout: value })}
              helperText="Time in minutes before user session expires"
              required
            />

            <div className="flex items-center justify-between py-2">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Maintenance Mode
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Temporarily disable access for system maintenance
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={systemSettings.maintenanceMode}
                  onChange={(e) => setSystemSettings({ ...systemSettings, maintenanceMode: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        </AdminCard>

        {/* Performance Settings */}
        <AdminCard
          title="Performance Settings"
          description="Optimize application performance and resource usage"
        >
          <div className="space-y-4">
            <FormField
              label="Cache Duration"
              name="cacheDuration"
              type="number"
              value={systemSettings.cacheDuration}
              onChange={(value) => setSystemSettings({ ...systemSettings, cacheDuration: value })}
              helperText="Time in hours to cache static resources"
              required
            />

            <div className="flex items-center justify-between py-2">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Enable Compression
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Compress responses to reduce bandwidth usage
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={systemSettings.enableCompression}
                  onChange={(e) => setSystemSettings({ ...systemSettings, enableCompression: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  CDN Integration
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Use content delivery network for static assets
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={systemSettings.enableCDN}
                  onChange={(e) => setSystemSettings({ ...systemSettings, enableCDN: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        </AdminCard>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveSystemSettings}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
        >
          <Save className="h-4 w-4 mr-2" />
          Save Settings
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'setup-home':
        return <SetupHome onNavigateToSection={handleNavigation} />;

      case 'object-manager':
        return (
          <>
            {!selectedObjectId ? (
              <ObjectManagerHome onSelectObject={(id) => navigate(buildAdminPath('object-manager', { objectId: id }))} />
            ) : (
              <ObjectDetail
                objectId={selectedObjectId}
                onBack={() => navigate(buildAdminPath('object-manager'))}
                onNavigateToPageLayouts={(layoutId, objectName) =>
                  handleNavigateToPageLayouts(layoutId, {
                    id: selectedObjectId,
                    name: objectName || 'Object'
                  })
                }
                onNavigateToTabConfig={(configId, objectName) =>
                  handleNavigateToTabConfig(configId, {
                    id: selectedObjectId,
                    name: objectName || 'Object'
                  })
                }
              />
            )}
          </>
        );

      case 'data-retention':
        return <DataRetentionManager />;

      case 'data-sensitivity':
        return <DataSensitivityManager />;

      case 'bulk-data':
        return (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Bulk Data Operations</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Import and export data via CSV, manage bulk operations, and track job history for Salesforce migration.
              </p>
            </div>
            <BulkDataManager />
          </>
        );

      case 'email-templates':
        return (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Email Templates</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Create and manage email templates used throughout the system.
              </p>
            </div>
            <EmailTemplatesHome />
          </>
        );
      case 'notes-admin':
        return (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Notes Management</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Create and manage notes with rich text, tags, and attachments.
              </p>
            </div>
            <NotesHome />
          </>
        );
      case 'ui-config':
        return (
          <>
            {/* Breadcrumb when navigated from Object Manager */}
            {navigationSource?.from === 'object-manager' && (
              <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2 text-sm">
                <button
                  onClick={handleBackToObjectManager}
                  className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 flex items-center gap-1 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to {navigationSource.objectName} in Object Manager
                </button>
              </div>
            )}
            <UIConfigurationManager userRole={userRole || 'admin'} initialTab="pages" />
          </>
        );
      case 'navigation':
        return (
          <div className="p-6">
            <NavigationConfigManager />
          </div>
        );
      case 'app-settings':
        return <AppSettingsHome />;

      case 'system-settings':
        return (
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Settings
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mt-2">
                Configure themes, branding, display preferences, and system behaviors
              </p>
            </div>
            <ThemeSettingsManager />
          </div>
        );
      case 'system':
        return (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">System Settings</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Configure application-wide settings and performance options.
              </p>
            </div>
            {renderSystemSettings()}
          </>
        );
      case 'database':
        return (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Database Management</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Configure database connections, manage backups, and monitor database performance.
              </p>
            </div>
            <DatabaseTab />
          </>
        );
      case 'monitoring':
        return (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">System Monitoring</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Monitor system performance, track metrics, and analyze usage trends.
              </p>
            </div>
            <MonitoringTab />
          </>
        );
      case 'system-security':
        return (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">System Security</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Manage system security policies, infrastructure access controls, and threat detection.
              </p>
            </div>
            <SecurityTab />
          </>
        );
      case 'api':
        return (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">API Management</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Configure API endpoints, manage rate limiting, and monitor API usage.
              </p>
            </div>
            <APIManagementTab />
          </>
        );
      case 'logs':
        return (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">System Logs</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                View and analyze system logs, track errors, and export log data.
              </p>
            </div>
            <SystemLogsTab />
          </>
        );

      // ============================================
      // ADMINISTRATION SECTION - PLACEHOLDERS
      // ============================================
      case 'users':
        return <UsersHome />;
      case 'roles':
        return <RolesHome />;
      case 'security-access':
        return <SecurityAccessHome onNavigate={handleNavigation} />;

      case 'field-level-security':
        return <SecurityHealthDashboard />;

      // ============================================
      // PROCESS AUTOMATION SECTION - PLACEHOLDERS
      // ============================================
      case 'approval-processes':
        return (
          <StandardToastProvider>
            {approvalProcessesView === 'list' && (
              <ApprovalProcessesList
                onCreateProcess={() => navigate(buildAdminPath('approval-processes', { approvalView: 'new' }))}
                onEditProcess={(processId) => {
                  navigate(buildAdminPath('approval-processes', { approvalView: 'detail', approvalId: processId }));
                  setApprovalEditMode(true);
                }}
                onViewProcess={(processId) => navigate(buildAdminPath('approval-processes', { approvalView: 'detail', approvalId: processId }))}
                onViewPendingApprovals={() => navigate(buildAdminPath('approval-processes', { approvalView: 'pending' }))}
              />
            )}
            {approvalProcessesView === 'new' && (
              <ApprovalProcessWizard
                onSave={() => navigate(buildAdminPath('approval-processes'))}
                onCancel={() => navigate(buildAdminPath('approval-processes'))}
              />
            )}
            {approvalProcessesView === 'edit' && selectedApprovalProcessId && (
              <ApprovalProcessWizard
                processId={selectedApprovalProcessId}
                onSave={() => { setApprovalEditMode(false); navigate(buildAdminPath('approval-processes')); }}
                onCancel={() => { setApprovalEditMode(false); navigate(buildAdminPath('approval-processes')); }}
              />
            )}
            {approvalProcessesView === 'detail' && selectedApprovalProcessId && (
              <ApprovalProcessDetail
                processId={selectedApprovalProcessId}
                onBack={() => navigate(buildAdminPath('approval-processes'))}
                onEdit={() => setApprovalEditMode(true)}
              />
            )}
            {approvalProcessesView === 'pending' && (
              <PendingApprovalsQueue
                onViewRequest={(requestId) => {
                  console.log('View request:', requestId);
                }}
                onViewRecord={(recordId, objectType) => {
                  console.log('View record:', recordId, objectType);
                }}
              />
            )}
          </StandardToastProvider>
        );
      case 'process-builder': {
        const handleProcessCloneFromDetail = async (process: Process) => {
          const cloned = await processBuilderStorageRouter.cloneProcessAsync({
            sourceProcessId: process.id,
            newName: `${process.name} (Copy)`
          });
          if (cloned) {
            setProcessBuilderRefreshKey(k => k + 1);
            navigate(buildAdminPath('process-builder', { processId: cloned.id }));
            setProcessBuilderEditMode(true);
          }
        };

        const handleProcessDeleteFromDetail = async (process: Process) => {
          await processBuilderStorageRouter.deleteProcessAsync(process.id);
          setProcessBuilderRefreshKey(k => k + 1);
          navigate(buildAdminPath('process-builder'));
        };

        const handleRefreshExecutions = () => {
          if (selectedProcessId) {
            setProcessExecutions(
              USE_GOBODO_API ? [] : processBuilderStorage.getExecutionsForProcess(selectedProcessId)
            );
          }
        };

        return (
          <StandardToastProvider>
            {processBuilderView === 'list' && (
              <ProcessBuilderList
                key={processBuilderRefreshKey}
                onCreateProcess={() => navigate(buildAdminPath('process-builder', { processId: 'new' }))}
                onEditProcess={(processId) => {
                  navigate(buildAdminPath('process-builder', { processId }));
                  setProcessBuilderEditMode(true);
                }}
                onViewProcess={(processId) => navigate(buildAdminPath('process-builder', { processId }))}
              />
            )}
            {processBuilderView === 'create' && (
              <ProcessBuilderWizard
                onSave={() => { setProcessBuilderRefreshKey(k => k + 1); navigate(buildAdminPath('process-builder')); }}
                onCancel={() => navigate(buildAdminPath('process-builder'))}
              />
            )}
            {processBuilderView === 'edit' && selectedProcessId && (
              <ProcessBuilderWizard
                processId={selectedProcessId}
                onSave={() => { setProcessBuilderRefreshKey(k => k + 1); navigate(buildAdminPath('process-builder')); }}
                onCancel={() => { setProcessBuilderEditMode(false); navigate(buildAdminPath('process-builder', { processId: selectedProcessId })); }}
              />
            )}
            {processBuilderView === 'detail' && selectedProcess && (
              <ProcessBuilderDetail
                process={selectedProcess}
                executions={processExecutions}
                onBack={() => navigate(buildAdminPath('process-builder'))}
                onEdit={() => setProcessBuilderEditMode(true)}
                onClone={handleProcessCloneFromDetail}
                onDelete={handleProcessDeleteFromDetail}
                onActivate={async (process) => {
                  const updated = await processBuilderStorageRouter.activateProcessAsync(process.id);
                  setProcessBuilderRefreshKey(k => k + 1);
                  if (updated) setSelectedProcess(updated);
                }}
                onDeactivate={async (process) => {
                  const updated = await processBuilderStorageRouter.deactivateProcessAsync(process.id);
                  setProcessBuilderRefreshKey(k => k + 1);
                  if (updated) setSelectedProcess(updated);
                }}
                onRefreshExecutions={handleRefreshExecutions}
              />
            )}
          </StandardToastProvider>
        );
      }
      case 'flow-automation': {
        const handleFlowClone = async () => {
          if (!selectedFlow) return;
          const cloned = await flowAutomationStorageRouter.cloneFlowAsync({
            sourceFlowId: selectedFlow.id,
            newName: `${selectedFlow.name} (Copy)`
          });
          if (cloned) {
            setFlowAutomationRefreshKey(k => k + 1);
            navigate(buildAdminPath('flow-automation', { flowId: cloned.id }));
            setFlowEditMode(true);
          }
        };

        const handleFlowDelete = async () => {
          if (!selectedFlow) return;
          await flowAutomationStorageRouter.deleteFlowAsync(selectedFlow.id);
          setFlowAutomationRefreshKey(k => k + 1);
          navigate(buildAdminPath('flow-automation'));
        };

        const handleFlowRefreshRuns = () => {
          if (selectedFlowId) {
            setFlowRuns(USE_GOBODO_API ? [] : flowAutomationStorage.getFlowRuns(selectedFlowId));
          }
        };

        return (
          <StandardToastProvider>
            {flowAutomationView === 'list' && (
              <FlowList
                key={flowAutomationRefreshKey}
                onCreateFlow={() => navigate(buildAdminPath('flow-automation', { flowId: 'new' }))}
                onEditFlow={(flowId) => {
                  navigate(buildAdminPath('flow-automation', { flowId }));
                  setFlowEditMode(true);
                }}
                onViewFlow={(flowId) => navigate(buildAdminPath('flow-automation', { flowId }))}
              />
            )}
            {flowAutomationView === 'create' && (
              <FlowWizard
                onSave={(flowId) => {
                  setFlowAutomationRefreshKey(k => k + 1);
                  navigate(buildAdminPath('flow-automation', { flowId }));
                }}
                onCancel={() => navigate(buildAdminPath('flow-automation'))}
              />
            )}
            {flowAutomationView === 'edit' && selectedFlow && (
              <FlowWizard
                flowId={selectedFlow.id}
                onSave={async (flowId) => {
                  setFlowAutomationRefreshKey(k => k + 1);
                  const updated = await flowAutomationStorageRouter.getFlowAsync(flowId);
                  setSelectedFlow(updated || null);
                  setFlowEditMode(false);
                }}
                onCancel={() => { setFlowEditMode(false); }}
              />
            )}
            {flowAutomationView === 'detail' && selectedFlow && (
              <FlowDetail
                flow={selectedFlow}
                runs={flowRuns}
                onBack={() => navigate(buildAdminPath('flow-automation'))}
                onEdit={() => setFlowEditMode(true)}
                onClone={handleFlowClone}
                onDelete={handleFlowDelete}
                onActivate={async () => {
                  if (!selectedFlow) return;
                  const updated = await flowAutomationStorageRouter.activateFlowAsync(selectedFlow.id);
                  setFlowAutomationRefreshKey(k => k + 1);
                  if (updated) setSelectedFlow(updated);
                }}
                onDeactivate={async () => {
                  if (!selectedFlow) return;
                  const updated = await flowAutomationStorageRouter.deactivateFlowAsync(selectedFlow.id);
                  setFlowAutomationRefreshKey(k => k + 1);
                  if (updated) setSelectedFlow(updated);
                }}
                onRefreshRuns={handleFlowRefreshRuns}
              />
            )}
          </StandardToastProvider>
        );
      }
      case 'scheduled-jobs':
        return (
          <StandardToastProvider>
            {scheduledJobsView === 'list' && (
              <ScheduledJobsList
                onCreateJob={() => navigate(buildAdminPath('scheduled-jobs', { jobId: 'new' }))}
                onEditJob={(jobId) => {
                  navigate(buildAdminPath('scheduled-jobs', { jobId }));
                  setJobEditMode(true);
                }}
                onViewJob={(jobId) => navigate(buildAdminPath('scheduled-jobs', { jobId }))}
              />
            )}
            {scheduledJobsView === 'create' && (
              <ScheduledJobWizard
                onSave={() => navigate(buildAdminPath('scheduled-jobs'))}
                onCancel={() => navigate(buildAdminPath('scheduled-jobs'))}
              />
            )}
            {scheduledJobsView === 'edit' && selectedJobId && (
              <ScheduledJobWizard
                jobId={selectedJobId}
                onSave={() => { setJobEditMode(false); navigate(buildAdminPath('scheduled-jobs')); }}
                onCancel={() => { setJobEditMode(false); navigate(buildAdminPath('scheduled-jobs', { jobId: selectedJobId })); }}
              />
            )}
            {scheduledJobsView === 'detail' && selectedJobId && (
              <JobDetailView
                jobId={selectedJobId}
                onBack={() => navigate(buildAdminPath('scheduled-jobs'))}
                onEdit={() => setJobEditMode(true)}
              />
            )}
          </StandardToastProvider>
        );

      // ============================================
      // DATA MANAGEMENT SECTION - PLACEHOLDERS
      // ============================================
      case 'data-import':
        return (
          <ComingSoonPlaceholder
            icon={Download}
            title="Data Import Coming Soon"
            description="Import data from various sources"
            featureList={[
              'Import from CSV and Excel files',
              'Map fields from source to destination',
              'Validate data before import',
              'Import in bulk with batch processing',
              'Schedule recurring imports',
              'Track import history and success rates'
            ]}
          />
        );
      case 'data-export':
        return (
          <ComingSoonPlaceholder
            icon={Upload}
            title="Data Export Coming Soon"
            description="Export data for backup and analysis"
            featureList={[
              'Export data to CSV, Excel, or JSON formats',
              'Select specific fields and records to export',
              'Schedule automatic exports',
              'Export related records together',
              'Encrypt exports for security',
              'Track export history and downloads'
            ]}
          />
        );
      case 'mass-delete':
        return <MassDeleteManager />;
      case 'workflow-rules':
        return <WorkflowRulesList />;

      case 'component-library':
        return <ComponentLibraryDemo />;

      default:
        return (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Data Retention</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Manage data lifecycle, deletion requests, and compliance requirements across all jurisdictions.
              </p>
            </div>
            <DataRetentionDashboard userRole={userRole} />
          </>
        );
    }
  };

  // ============================================
  // NAVIGATION HANDLERS FOR CROSS-SECTION ROUTING
  // ============================================

  const handleNavigateToPageLayouts = (layoutId?: string, fromObject?: { id: string; name: string }) => {
    // Page Layouts are now managed within Object Manager only
    // This handler is kept for compatibility but does nothing
    console.log('Page Layouts are managed in Object Manager. Use the Visual Builder within Object Manager instead.');
  };

  const handleNavigateToTabConfig = (configId?: string, fromObject?: { id: string; name: string }) => {
    navigate(buildAdminPath('ui-config'));
    setUiConfigTab('tab-configuration');

    // Track navigation source for breadcrumb
    if (fromObject) {
      setNavigationSource({
        from: 'object-manager',
        objectId: fromObject.id,
        objectName: fromObject.name
      });
    }

    if (configId && configId !== 'new') {
      console.log('Should open tab config editor for:', configId);
      // Future: Pass configId to TabConfigurationManager to auto-open editor
    }

    console.log('Navigated to Tab Configuration', configId ? `- config ${configId}` : '');
  };

  const handleBackToObjectManager = () => {
    if (navigationSource?.objectId) {
      navigate(buildAdminPath('object-manager', { objectId: navigationSource.objectId }));
      setNavigationSource(null);
    }
  };

  const handleNavigation = (view: string) => {
    const mainAppViews = ['home', 'profile', 'settings', 'support-tickets'];
    if (mainAppViews.includes(view) && onNavigate) {
      onNavigate(view);
    } else {
      setNavigationSource(null);
      navigate(buildAdminPath(view));
    }
  };

  return (
    <RecentItemsProvider>
      <>
        <AdminLayout
          activeSection={activeSection}
          onNavigate={handleNavigation}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
          userRole={userRole}
          onRoleChange={onRoleChange}
        >
          {renderContent()}
        </AdminLayout>

      {/* Layout Preview Modal */}
      {showLayoutPreview && selectedPageLayoutApp && (
        <div className="fixed inset-0 bg-white dark:bg-gray-900 z-50 overflow-y-auto">
          {/* Preview Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-10">
            <div className="max-w-7xl mx-auto px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    Layout Preview - {selectedPageLayoutApp === 'global' && 'Global View'}
                    {selectedPageLayoutApp === 'regal-main' && 'Regal Main'}
                    {selectedPageLayoutApp === 'credit-management' && 'Credit Management'}
                    {selectedPageLayoutApp === 'cybersweep' && 'CyberSweep'}
                    {selectedPageLayoutApp === 'credit-block' && 'Credit Block'}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    This is how users will see the layout in production. Drag sections to reorder them within tabs.
                  </p>

                  {/* Page Selector */}
                  <div className="mt-4 flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Preview Page:
                    </label>
                    <select
                      value={previewPage}
                      onChange={(e) => {
                        setPreviewPage(e.target.value);
                        // Reset to first tab when page changes
                        const tabs = getTabsForPage(pageSections, e.target.value, selectedPageLayoutApp || '');
                        if (tabs.length > 0) {
                          setPreviewActiveTab(tabs[0].id);
                        }
                      }}
                      className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {(() => {
                        // Get all unique pages that have sections configured
                        const configuredPages = new Set<string>();
                        pageSections.forEach(section => {
                          if (section.application === selectedPageLayoutApp && section.page && section.visible) {
                            configuredPages.add(section.page);
                          }
                        });

                        // If no pages configured, show default options
                        if (configuredPages.size === 0) {
                          return (
                            <>
                              <option value="client-detail">Client Detail</option>
                              <option value="account-detail">Account Detail</option>
                              <option value="contact-detail">Contact Detail</option>
                              <option value="opportunity-detail">Opportunity Detail</option>
                            </>
                          );
                        }

                        // Show configured pages
                        return Array.from(configuredPages).sort().map(page => {
                          // Format page name for display
                          const displayName = page
                            .split(/[-_]/)
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ');

                          return (
                            <option key={page} value={page}>
                              {displayName}
                            </option>
                          );
                        });
                      })()}
                    </select>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowLayoutPreview(false);
                    setCollapsedSections(new Set());
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                  Exit Preview
                </button>
              </div>
            </div>
          </div>

          {/* Preview Content */}
          <div className="max-w-7xl mx-auto px-6 py-8">
            {(() => {
              // Get tabs for the selected page
              const tabs = getTabsForPage(pageSections, previewPage, selectedPageLayoutApp || '');

              // If no tabs, show message
              if (tabs.length === 0) {
                return (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                    <ExternalLink className="h-16 w-16 mx-auto mb-4 text-gray-400 dark:text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      No Sections Configured
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      Configure sections for the "{previewPage}" page to preview them here
                    </p>
                  </div>
                );
              }

              // Ensure active tab exists in current tabs
              if (!tabs.find(t => t.id === previewActiveTab)) {
                setPreviewActiveTab(tabs[0].id);
              }

              const activeTab = tabs.find(t => t.id === previewActiveTab) || tabs[0];

              return (
                <div className="space-y-6">
                  {/* Tab Navigation */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-2">
                    <div className="flex gap-2 overflow-x-auto">
                      {tabs.map((tab) => {
                        const Icon = require('lucide-react')[getTabIcon(tab.id)] || require('lucide-react')['FileText'];
                        return (
                          <button
                            key={tab.id}
                            onClick={() => setPreviewActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                              previewActiveTab === tab.id
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            {tab.label}
                            <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                              {tab.sections.length}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Draggable Sections for Active Tab */}
                  <DragDropContext
                    onDragEnd={(result: DropResult) => {
                      if (!result.destination) return;

                      const sourceIndex = result.source.index;
                      const destIndex = result.destination.index;

                      if (sourceIndex === destIndex) return;

                      // Get sections for the active tab
                      const tabSections = activeTab.sections;

                      // Reorder sections
                      const reorderedSections = Array.from(tabSections);
                      const [removed] = reorderedSections.splice(sourceIndex, 1);
                      reorderedSections.splice(destIndex, 0, removed);

                      // Update order property for all sections
                      const updatedSections = pageSections.map(section => {
                        const newOrderIndex = reorderedSections.findIndex(s => s.id === section.id);
                        if (newOrderIndex !== -1) {
                          return { ...section, order: newOrderIndex };
                        }
                        return section;
                      });

                      // Save to localStorage
                      const layoutData = {
                        sections: updatedSections,
                        lastModified: new Date().toISOString()
                      };
                      localStorage.setItem(`layout-${selectedPageLayoutApp}`, JSON.stringify(layoutData));

                      // Force re-render by updating state
                      setHasUnsavedChanges(false); // Trigger parent reload if needed

                      success('Sections reordered', 'Section order updated successfully');
                    }}
                  >
                    <Droppable droppableId="preview-sections">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="space-y-4"
                        >
                          {activeTab.sections.map((section, index) => {
                            const isCollapsed = collapsedSections.has(section.id);

                            return (
                              <Draggable
                                key={section.id}
                                draggableId={section.id}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden ${
                                      snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-500' : ''
                                    }`}
                                  >
                                    {/* Section Header with Drag Handle */}
                                    <div
                                      className={`p-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3 ${
                                        section.collapsible ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' : ''
                                      }`}
                                    >
                                      <div
                                        {...provided.dragHandleProps}
                                        className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                                        title="Drag to reorder"
                                      >
                                        <GripVertical className="h-5 w-5 text-gray-400" />
                                      </div>

                                      <div
                                        className="flex-1 flex items-center justify-between"
                                        onClick={() => {
                                          if (section.collapsible) {
                                            const newCollapsed = new Set(collapsedSections);
                                            if (isCollapsed) {
                                              newCollapsed.delete(section.id);
                                            } else {
                                              newCollapsed.add(section.id);
                                            }
                                            setCollapsedSections(newCollapsed);
                                          }
                                        }}
                                      >
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                          {section.name}
                                        </h3>
                                        {section.collapsible && (
                                          <button className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                            {isCollapsed ? (
                                              <ChevronDown className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                            ) : (
                                              <ChevronUp className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                            )}
                                          </button>
                                        )}
                                      </div>
                                    </div>

                                    {/* Section Content */}
                                    {!isCollapsed && (
                                      <div className="p-6">
                                        {section.fields.length === 0 ? (
                                          <p className="text-sm text-gray-500 dark:text-gray-400 italic text-center py-8">
                                            No fields configured for this section
                                          </p>
                                        ) : (
                                          <div className={`grid ${section.columns === 1 ? 'grid-cols-1' : section.columns === 2 ? 'grid-cols-2' : 'grid-cols-3'} gap-4`}>
                                            {section.fields.filter(f => f.visible).map((field) => (
                                              <div
                                                key={field.id}
                                                className={`space-y-2 ${field.width === 100 ? section.columns === 1 ? 'col-span-1' : section.columns === 2 ? 'col-span-2' : 'col-span-3' : ''}`}
                                              >
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                  {field.label}
                                                  {field.required && <span className="text-red-500 ml-1">*</span>}
                                                  {field.pii && (
                                                    <span className="ml-2 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded">
                                                      PII
                                                    </span>
                                                  )}
                                                  {field.encrypted && (
                                                    <span className="ml-1 text-purple-600 dark:text-purple-400">🔒</span>
                                                  )}
                                                </label>
                                                {field.type === 'select' ? (
                                                  <select
                                                    disabled
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-not-allowed"
                                                  >
                                                    <option>Select {field.label.toLowerCase()}</option>
                                                  </select>
                                                ) : field.type === 'date' || field.type === 'datetime' ? (
                                                  <input
                                                    type={field.type === 'datetime' ? 'datetime-local' : 'date'}
                                                    disabled
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-not-allowed"
                                                  />
                                                ) : (
                                                  <input
                                                    type={field.type === 'ssn' ? 'password' : field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : field.type === 'number' ? 'number' : 'text'}
                                                    placeholder={`Enter ${field.label.toLowerCase()}`}
                                                    disabled
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-not-allowed"
                                                  />
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              );
            })()}
          </div>
        </div>
      )}

    </>
    </RecentItemsProvider>
  );
};

export default SystemAdmin;